import {
  importWebSocket,
  logger
} from "./chunk-Y7PO43SB.js";
import {
  ACTOR_VERSIONED,
  ActionContext,
  BeforeConnectContext,
  CONN_CONNECTED_SYMBOL,
  CONN_DRIVER_SYMBOL,
  CONN_SEND_MESSAGE_SYMBOL,
  CONN_SPEAKS_RIVETKIT_SYMBOL,
  CONN_STATE_MANAGER_SYMBOL,
  CONN_VERSIONED,
  CURRENT_VERSION,
  CURRENT_VERSION2,
  CachedSerializer,
  Conn,
  ConnectContext,
  CreateConnStateContext,
  DeadlineError,
  EncodingSchema,
  HEADER_CONN_PARAMS,
  HEADER_ENCODING,
  HEADER_RIVET_TOKEN,
  HTTP_ACTION_REQUEST_VERSIONED,
  HTTP_ACTION_RESPONSE_VERSIONED,
  HTTP_QUEUE_SEND_REQUEST_VERSIONED,
  HTTP_QUEUE_SEND_RESPONSE_VERSIONED,
  HTTP_RESPONSE_ERROR_VERSIONED,
  HttpActionRequestSchema,
  HttpActionResponseSchema,
  HttpQueueSendRequestSchema,
  HttpQueueSendResponseSchema,
  HttpResponseErrorSchema,
  PATH_CONNECT,
  PATH_WEBSOCKET_PREFIX,
  QUEUE_MESSAGE_VERSIONED,
  QUEUE_METADATA_VERSIONED,
  RequestContext,
  TO_CLIENT_VERSIONED,
  TO_SERVER_VERSIONED,
  ToClientSchema,
  ToServerSchema,
  WS_PROTOCOL_CONN_PARAMS,
  WS_PROTOCOL_ENCODING,
  WS_PROTOCOL_STANDARD,
  WebSocketContext,
  assertUnreachable as assertUnreachable2,
  contentTypeForEncoding,
  createHttpDriver,
  deadline,
  deserializeActorKey,
  deserializeWithEncoding,
  generateSecureToken,
  getRunFunction,
  getRunInspectorConfig,
  inputDataToBuffer,
  isConnStatePath,
  isStatePath,
  jsonStringifyCompat,
  processMessage,
  serializeActorKey,
  serializeWithEncoding,
  tryParseEndpoint,
  uint8ArrayToBase64
} from "./chunk-KAQ6FLH6.js";
import {
  ActorContext,
  KEYS,
  decodeQueueMessageKey,
  makeConnKey,
  makeQueueMessageKey
} from "./chunk-7LY4X5LS.js";
import {
  EXTRA_ERROR_LOG,
  SinglePromiseQueue,
  VERSION,
  arrayBuffersEqual,
  assertUnreachable,
  bufferToArrayBuffer,
  combineUrlPath,
  deconstructError,
  getBaseLogger,
  getIncludeTarget,
  getLogMessage,
  getLogger,
  getNextPhase,
  getRivetEndpoint,
  getRivetEngine,
  getRivetNamespace,
  getRivetRunner,
  getRivetToken,
  httpUserAgent,
  isCborSerializable,
  noopNext,
  promiseWithResolvers,
  stringifyError
} from "./chunk-TR5V2X4T.js";
import {
  ActionNotFound,
  ActionTimedOut,
  ActorAborted,
  ActorNotFound,
  DatabaseNotEnabled,
  InternalError,
  InvalidRequest,
  InvalidRequestHandlerResponse,
  InvalidStateType,
  OutgoingMessageTooLong,
  QueueAlreadyCompleted,
  QueueFull,
  QueueMessageInvalid,
  QueueMessagePending,
  QueueMessageTooLarge,
  RequestHandlerNotDefined,
  StateNotEnabled,
  VarsNotEnabled
} from "./chunk-PY7SOKTM.js";

// src/devtools-loader/log.ts
function logger2() {
  return getLogger("devtools");
}

// src/devtools-loader/index.ts
var DEFAULT_DEVTOOLS_URL = (version = VERSION) => `https://releases.rivet.dev/rivet/latest/devtools/mod.js?v=${version}`;
var scriptId = "rivetkit-devtools-script";
function injectDevtools(config) {
  if (!window) {
    logger2().warn("devtools not available outside browser environment");
    return;
  }
  if (!document.getElementById(scriptId)) {
    const script = document.createElement("script");
    script.id = scriptId;
    script.src = DEFAULT_DEVTOOLS_URL();
    script.async = true;
    document.head.appendChild(script);
  }
  window.__rivetkit = window.__rivetkit || [];
  window.__rivetkit.push(config);
}

// src/remote-manager-driver/mod.ts
import * as cbor2 from "cbor-x";
import invariant2 from "invariant";

// src/remote-manager-driver/api-utils.ts
import { z } from "zod/v4";

// src/client/utils.ts
import * as cbor from "cbor-x";
import invariant from "invariant";

// src/client/errors.ts
var ActorClientError = class extends Error {
};
var InternalError2 = class extends ActorClientError {
};
var ManagerError = class extends ActorClientError {
  constructor(error, opts) {
    super(`Manager error: ${error}`, opts);
  }
};
var MalformedResponseMessage = class extends ActorClientError {
  constructor(cause) {
    super(`Malformed response message: ${cause}`, { cause });
  }
};
var ActorError = class extends ActorClientError {
  constructor(group, code, message, metadata) {
    super(message);
    this.group = group;
    this.code = code;
    this.metadata = metadata;
  }
  __type = "ActorError";
};
var HttpRequestError = class extends ActorClientError {
  constructor(message, opts) {
    super(`HTTP request error: ${message}`, { cause: opts == null ? void 0 : opts.cause });
  }
};
var ActorConnDisposed = class extends ActorClientError {
  constructor() {
    super("Attempting to interact with a disposed actor connection.");
  }
};
function isSchedulingError(group, code) {
  return group === "guard" && (code === "actor_ready_timeout" || code === "actor_runner_failed");
}
var ActorSchedulingError = class extends ActorError {
  actorId;
  details;
  constructor(group, code, actorId, details) {
    super(
      group,
      code,
      `Actor failed to start (${actorId}): ${JSON.stringify(details)}`,
      { actorId, details }
    );
    this.name = "ActorSchedulingError";
    this.actorId = actorId;
    this.details = details;
  }
};

// src/client/utils.ts
function parseWebSocketCloseReason(reason) {
  const [mainPart, rayId] = reason.split("#");
  const [group, code] = mainPart.split(".");
  if (!group || !code) {
    logger().warn({ msg: "failed to parse close reason", reason });
    return void 0;
  }
  return {
    group,
    code,
    rayId
  };
}
function messageLength(message) {
  if (message instanceof Blob) {
    return message.size;
  }
  if (message instanceof ArrayBuffer) {
    return message.byteLength;
  }
  if (message instanceof Uint8Array) {
    return message.byteLength;
  }
  if (typeof message === "string") {
    return message.length;
  }
  assertUnreachable(message);
}
async function sendHttpRequest(opts) {
  logger().debug({
    msg: "sending http request",
    url: opts.url,
    encoding: opts.encoding
  });
  let contentType;
  let bodyData;
  if (opts.method === "POST" || opts.method === "PUT") {
    invariant(opts.body !== void 0, "missing body");
    contentType = contentTypeForEncoding(opts.encoding);
    bodyData = serializeWithEncoding(
      opts.encoding,
      opts.body,
      opts.requestVersionedDataHandler,
      opts.requestVersion,
      opts.requestZodSchema,
      opts.requestToJson,
      opts.requestToBare
    );
  }
  let response;
  try {
    response = await (opts.customFetch ?? fetch)(
      new globalThis.Request(opts.url, {
        method: opts.method,
        headers: {
          ...opts.headers,
          ...contentType ? {
            "Content-Type": contentType
          } : {},
          "User-Agent": httpUserAgent()
        },
        body: bodyData,
        credentials: "include",
        signal: opts.signal
      })
    );
  } catch (error) {
    throw new HttpRequestError(`Request failed: ${error}`, {
      cause: error
    });
  }
  if (!response.ok) {
    const bufferResponse = await response.arrayBuffer();
    const contentType2 = response.headers.get("content-type");
    const rayId = response.headers.get("x-rivet-ray-id");
    const encoding = (contentType2 == null ? void 0 : contentType2.includes("application/json")) ? "json" : opts.encoding;
    try {
      const responseData = deserializeWithEncoding(
        encoding,
        new Uint8Array(bufferResponse),
        HTTP_RESPONSE_ERROR_VERSIONED,
        HttpResponseErrorSchema,
        // JSON: metadata is already unknown
        (json) => json,
        // BARE: decode ArrayBuffer metadata to unknown
        (bare) => ({
          group: bare.group,
          code: bare.code,
          message: bare.message,
          metadata: bare.metadata ? cbor.decode(new Uint8Array(bare.metadata)) : void 0
        })
      );
      throw new ActorError(
        responseData.group,
        responseData.code,
        responseData.message,
        responseData.metadata
      );
    } catch (error) {
      if (error instanceof ActorError) {
        throw error;
      }
      const textResponse = new TextDecoder("utf-8", {
        fatal: false
      }).decode(bufferResponse);
      if (rayId) {
        throw new HttpRequestError(
          `${response.statusText} (${response.status}) (Ray ID: ${rayId}):
${textResponse}`
        );
      } else {
        throw new HttpRequestError(
          `${response.statusText} (${response.status}):
${textResponse}`
        );
      }
    }
  }
  if (opts.skipParseResponse) {
    return void 0;
  }
  try {
    const buffer = new Uint8Array(await response.arrayBuffer());
    return deserializeWithEncoding(
      opts.encoding,
      buffer,
      opts.responseVersionedDataHandler,
      opts.responseZodSchema,
      opts.responseFromJson,
      opts.responseFromBare
    );
  } catch (error) {
    throw new HttpRequestError(`Failed to parse response: ${error}`, {
      cause: error
    });
  }
}

// src/remote-manager-driver/log.ts
function logger3() {
  return getLogger("remote-manager-driver");
}

// src/remote-manager-driver/api-utils.ts
var EngineApiError = class extends Error {
  constructor(group, code, message) {
    super(message || `Engine API error: ${group}/${code}`);
    this.group = group;
    this.code = code;
    this.name = "EngineApiError";
  }
};
function getEndpoint(config) {
  return config.endpoint ?? "http://127.0.0.1:6420";
}
async function apiCall(config, method, path, body) {
  const endpoint = getEndpoint(config);
  const url = combineUrlPath(endpoint, path, {
    namespace: config.namespace
  });
  logger3().debug({ msg: "making api call", method, url });
  const headers = {
    ...config.headers
  };
  if (config.token) {
    headers.Authorization = `Bearer ${config.token}`;
  }
  return await sendHttpRequest({
    method,
    url,
    headers,
    body,
    encoding: "json",
    skipParseResponse: false,
    requestVersionedDataHandler: void 0,
    requestVersion: void 0,
    responseVersionedDataHandler: void 0,
    responseVersion: void 0,
    requestZodSchema: z.any(),
    responseZodSchema: z.any(),
    // Identity conversions (passthrough for generic API calls)
    requestToJson: (value) => value,
    requestToBare: (value) => value,
    responseFromJson: (value) => value,
    responseFromBare: (value) => value
  });
}

// src/remote-manager-driver/actor-websocket-client.ts
function buildActorGatewayUrl(endpoint, actorId, token, path = "") {
  const tokenSegment = token !== void 0 ? `@${encodeURIComponent(token)}` : "";
  const gatewayPath = `/gateway/${encodeURIComponent(actorId)}${tokenSegment}${path}`;
  return combineUrlPath(endpoint, gatewayPath);
}
async function openWebSocketToActor(runConfig, path, actorId, encoding, params) {
  const WebSocket2 = await importWebSocket();
  const endpoint = getEndpoint(runConfig);
  const guardUrl = buildActorGatewayUrl(
    endpoint,
    actorId,
    runConfig.token,
    path
  );
  logger3().debug({
    msg: "opening websocket to actor via guard",
    actorId,
    path,
    guardUrl
  });
  const ws = new WebSocket2(
    guardUrl,
    buildWebSocketProtocols(runConfig, encoding, params)
  );
  ws.binaryType = "arraybuffer";
  logger3().debug({ msg: "websocket connection opened", actorId });
  return ws;
}
function buildWebSocketProtocols(runConfig, encoding, params) {
  const protocols = [];
  protocols.push(WS_PROTOCOL_STANDARD);
  protocols.push(`${WS_PROTOCOL_ENCODING}${encoding}`);
  if (params) {
    protocols.push(
      `${WS_PROTOCOL_CONN_PARAMS}${encodeURIComponent(JSON.stringify(params))}`
    );
  }
  return protocols;
}

// src/remote-manager-driver/actor-http-client.ts
async function sendHttpRequestToActor(runConfig, actorId, actorRequest) {
  const url = new URL(actorRequest.url);
  const endpoint = getEndpoint(runConfig);
  const guardUrl = buildActorGatewayUrl(
    endpoint,
    actorId,
    runConfig.token,
    `${url.pathname}${url.search}`
  );
  let bodyToSend = null;
  const guardHeaders = buildGuardHeadersForHttp(
    runConfig,
    actorRequest,
    actorId
  );
  if (actorRequest.method !== "GET" && actorRequest.method !== "HEAD") {
    if (actorRequest.bodyUsed) {
      throw new Error("Request body has already been consumed");
    }
    const reqBody = await actorRequest.arrayBuffer();
    if (reqBody.byteLength !== 0) {
      bodyToSend = reqBody;
      guardHeaders.delete("transfer-encoding");
      guardHeaders.set("content-length", String(bodyToSend.byteLength));
    }
  }
  const guardRequest = new Request(guardUrl, {
    method: actorRequest.method,
    headers: guardHeaders,
    body: bodyToSend,
    signal: actorRequest.signal
  });
  return mutableResponse(await fetch(guardRequest));
}
function mutableResponse(fetchRes) {
  return new Response(fetchRes.body, fetchRes);
}
function buildGuardHeadersForHttp(runConfig, actorRequest, actorId) {
  const headers = new Headers();
  actorRequest.headers.forEach((value, key) => {
    headers.set(key, value);
  });
  for (const [key, value] of Object.entries(runConfig.headers)) {
    headers.set(key, value);
  }
  if (runConfig.token) {
    headers.set(HEADER_RIVET_TOKEN, runConfig.token);
  }
  return headers;
}

// src/remote-manager-driver/api-endpoints.ts
async function getActor(config, _, actorId) {
  return apiCall(
    config,
    "GET",
    `/actors?actor_ids=${encodeURIComponent(actorId)}`
  );
}
async function getActorByKey(config, name, key) {
  const serializedKey = serializeActorKey(key);
  return apiCall(
    config,
    "GET",
    `/actors?name=${encodeURIComponent(name)}&key=${encodeURIComponent(serializedKey)}`
  );
}
async function listActorsByName(config, name) {
  return apiCall(
    config,
    "GET",
    `/actors?name=${encodeURIComponent(name)}`
  );
}
async function getOrCreateActor(config, request) {
  return apiCall(
    config,
    "PUT",
    `/actors`,
    request
  );
}
async function createActor(config, request) {
  return apiCall(
    config,
    "POST",
    `/actors`,
    request
  );
}
async function destroyActor(config, actorId) {
  return apiCall(
    config,
    "DELETE",
    `/actors/${encodeURIComponent(actorId)}`
  );
}
async function getMetadata(config) {
  return apiCall(config, "GET", `/metadata`);
}
async function getDatacenters(config) {
  return apiCall(config, "GET", `/datacenters`);
}
async function updateRunnerConfig(config, runnerName, request) {
  return apiCall(
    config,
    "PUT",
    `/runner-configs/${runnerName}`,
    request
  );
}
async function kvGet(config, actorId, key) {
  return apiCall(
    config,
    "GET",
    `/actors/${encodeURIComponent(actorId)}/kv/keys/${encodeURIComponent(key)}`
  );
}

// src/remote-manager-driver/metadata.ts
import pRetry from "p-retry";
var metadataLookupCache = /* @__PURE__ */ new Map();
async function lookupMetadataCached(config) {
  const endpoint = getEndpoint(config);
  const existingPromise = metadataLookupCache.get(endpoint);
  if (existingPromise) {
    return existingPromise;
  }
  const metadataLookupPromise = pRetry(
    async () => {
      logger3().debug({
        msg: "fetching metadata",
        endpoint
      });
      const metadataData = await getMetadata(config);
      logger3().debug({
        msg: "received metadata",
        endpoint,
        clientEndpoint: metadataData.clientEndpoint
      });
      return metadataData;
    },
    {
      forever: true,
      minTimeout: 500,
      maxTimeout: 15e3,
      onFailedAttempt: (error) => {
        if (error.attemptNumber > 1) {
          logger3().warn({
            msg: "failed to fetch metadata, retrying",
            endpoint,
            attempt: error.attemptNumber,
            error: stringifyError(error)
          });
        }
      }
    }
  );
  metadataLookupCache.set(endpoint, metadataLookupPromise);
  return metadataLookupPromise;
}

// src/remote-manager-driver/ws-proxy.ts
async function createWebSocketProxy(c, targetUrl, protocols) {
  const WebSocket2 = await importWebSocket();
  const state = {};
  return {
    onOpen: async (event, clientWs) => {
      logger3().debug({ msg: "client websocket connected", targetUrl });
      if (clientWs.readyState !== 1) {
        logger3().warn({
          msg: "client websocket not open on connection",
          targetUrl,
          readyState: clientWs.readyState
        });
        return;
      }
      const targetWs = new WebSocket2(targetUrl, protocols);
      state.targetWs = targetWs;
      state.connectPromise = new Promise((resolve, reject) => {
        targetWs.addEventListener("open", () => {
          logger3().debug({
            msg: "target websocket connected",
            targetUrl
          });
          if (clientWs.readyState !== 1) {
            logger3().warn({
              msg: "client websocket closed before target connected",
              targetUrl,
              clientReadyState: clientWs.readyState
            });
            targetWs.close(1001, "Client disconnected");
            reject(new Error("Client disconnected"));
            return;
          }
          resolve();
        });
        targetWs.addEventListener("error", (error) => {
          logger3().warn({
            msg: "target websocket error during connection",
            targetUrl
          });
          reject(error);
        });
      });
      state.targetWs.addEventListener("message", (event2) => {
        if (typeof event2.data === "string" || event2.data instanceof ArrayBuffer) {
          clientWs.send(event2.data);
        } else if (event2.data instanceof Blob) {
          event2.data.arrayBuffer().then((buffer) => {
            clientWs.send(buffer);
          });
        }
      });
      state.targetWs.addEventListener("close", (event2) => {
        logger3().debug({
          msg: "target websocket closed",
          targetUrl,
          code: event2.code,
          reason: event2.reason
        });
        closeWebSocketIfOpen(clientWs, event2.code, event2.reason);
      });
      state.targetWs.addEventListener("error", (error) => {
        logger3().error({
          msg: "target websocket error",
          targetUrl,
          error: stringifyError(error)
        });
        closeWebSocketIfOpen(clientWs, 1011, "Target WebSocket error");
      });
    },
    onMessage: async (event, clientWs) => {
      if (!state.targetWs || !state.connectPromise) {
        logger3().error({
          msg: "websocket state not initialized",
          targetUrl
        });
        return;
      }
      try {
        await state.connectPromise;
        if (state.targetWs.readyState === WebSocket2.OPEN) {
          state.targetWs.send(event.data);
        } else {
          logger3().warn({
            msg: "target websocket not open",
            targetUrl,
            readyState: state.targetWs.readyState
          });
        }
      } catch (error) {
        logger3().error({
          msg: "failed to connect to target websocket",
          targetUrl,
          error
        });
        closeWebSocketIfOpen(
          clientWs,
          1011,
          "Failed to connect to target"
        );
      }
    },
    onClose: (event, clientWs) => {
      logger3().debug({
        msg: "client websocket closed",
        targetUrl,
        code: event.code,
        reason: event.reason,
        wasClean: event.wasClean
      });
      if (state.targetWs) {
        if (state.targetWs.readyState === WebSocket2.OPEN || state.targetWs.readyState === WebSocket2.CONNECTING) {
          state.targetWs.close(
            1e3,
            event.reason || "Client disconnected"
          );
        }
      }
    },
    onError: (event, clientWs) => {
      logger3().error({ msg: "client websocket error", targetUrl, event });
      if (state.targetWs) {
        if (state.targetWs.readyState === WebSocket2.OPEN) {
          state.targetWs.close(1011, "Client WebSocket error");
        } else if (state.targetWs.readyState === WebSocket2.CONNECTING) {
          state.targetWs.close();
        }
      }
    }
  };
}
function closeWebSocketIfOpen(ws, code, reason) {
  if (ws.readyState === 1) {
    ws.close(code, reason);
  } else if ("close" in ws && ws.readyState === WebSocket.OPEN) {
    ws.close(code, reason);
  }
}

// src/remote-manager-driver/mod.ts
var RemoteManagerDriver = class {
  #config;
  #metadataPromise;
  constructor(runConfig) {
    if (getNextPhase() === "phase-production-build") {
      logger3().info(
        "detected next.js build phase, disabling health check"
      );
      runConfig.disableMetadataLookup = true;
    }
    this.#config = { ...runConfig };
    if (!runConfig.disableMetadataLookup) {
      this.#metadataPromise = lookupMetadataCached(this.#config).then(
        (metadataData) => {
          if (metadataData.clientEndpoint) {
            this.#config.endpoint = metadataData.clientEndpoint;
            if (metadataData.clientNamespace) {
              this.#config.namespace = metadataData.clientNamespace;
            }
            if (metadataData.clientToken) {
              this.#config.token = metadataData.clientToken;
            }
            logger3().info({
              msg: "overriding client endpoint",
              endpoint: metadataData.clientEndpoint,
              namespace: metadataData.clientNamespace,
              token: metadataData.clientToken
            });
          }
          logger3().info({
            msg: "connected to rivetkit manager",
            runtime: metadataData.runtime,
            version: metadataData.version,
            runner: metadataData.runner
          });
        }
      );
    }
  }
  async getForId({
    c,
    name,
    actorId
  }) {
    if (this.#metadataPromise) {
      await this.#metadataPromise;
    }
    const response = await getActor(this.#config, name, actorId);
    const actor = response.actors[0];
    if (!actor) return void 0;
    if (actor.name !== name) {
      logger3().debug({
        msg: "actor name mismatch from api",
        actorId,
        apiName: actor.name,
        requestedName: name
      });
      return void 0;
    }
    return apiActorToOutput(actor);
  }
  async getWithKey({
    c,
    name,
    key
  }) {
    if (this.#metadataPromise) {
      await this.#metadataPromise;
    }
    logger3().debug({ msg: "getWithKey: searching for actor", name, key });
    try {
      const response = await getActorByKey(this.#config, name, key);
      const actor = response.actors[0];
      if (!actor) return void 0;
      logger3().debug({
        msg: "getWithKey: found actor via api",
        actorId: actor.actor_id,
        name,
        key
      });
      return apiActorToOutput(actor);
    } catch (error) {
      if (error instanceof EngineApiError && error.group === "actor" && error.code === "not_found") {
        return void 0;
      }
      throw error;
    }
  }
  async getOrCreateWithKey(input) {
    if (this.#metadataPromise) {
      await this.#metadataPromise;
    }
    const { c, name, key, input: actorInput, region } = input;
    logger3().info({
      msg: "getOrCreateWithKey: getting or creating actor via engine api",
      name,
      key
    });
    const { actor, created } = await getOrCreateActor(this.#config, {
      datacenter: region,
      name,
      key: serializeActorKey(key),
      runner_name_selector: this.#config.runnerName,
      input: actorInput ? uint8ArrayToBase64(cbor2.encode(actorInput)) : void 0,
      crash_policy: "sleep"
    });
    logger3().info({
      msg: "getOrCreateWithKey: actor ready",
      actorId: actor.actor_id,
      name,
      key,
      created
    });
    return apiActorToOutput(actor);
  }
  async createActor({
    c,
    name,
    key,
    input,
    region
  }) {
    if (this.#metadataPromise) {
      await this.#metadataPromise;
    }
    logger3().info({ msg: "creating actor via engine api", name, key });
    const result = await createActor(this.#config, {
      datacenter: region,
      name,
      runner_name_selector: this.#config.runnerName,
      key: serializeActorKey(key),
      input: input ? uint8ArrayToBase64(cbor2.encode(input)) : void 0,
      crash_policy: "sleep"
    });
    logger3().info({
      msg: "actor created",
      actorId: result.actor.actor_id,
      name,
      key
    });
    return apiActorToOutput(result.actor);
  }
  async listActors({ c, name }) {
    if (this.#metadataPromise) {
      await this.#metadataPromise;
    }
    logger3().debug({ msg: "listing actors via engine api", name });
    const response = await listActorsByName(this.#config, name);
    return response.actors.map(apiActorToOutput);
  }
  async destroyActor(actorId) {
    if (this.#metadataPromise) {
      await this.#metadataPromise;
    }
    logger3().info({ msg: "destroying actor via engine api", actorId });
    await destroyActor(this.#config, actorId);
    logger3().info({ msg: "actor destroyed", actorId });
  }
  async sendRequest(actorId, actorRequest) {
    if (this.#metadataPromise) {
      await this.#metadataPromise;
    }
    return await sendHttpRequestToActor(
      this.#config,
      actorId,
      actorRequest
    );
  }
  async openWebSocket(path, actorId, encoding, params) {
    if (this.#metadataPromise) {
      await this.#metadataPromise;
    }
    return await openWebSocketToActor(
      this.#config,
      path,
      actorId,
      encoding,
      params
    );
  }
  async buildGatewayUrl(actorId) {
    if (this.#metadataPromise) {
      await this.#metadataPromise;
    }
    const endpoint = getEndpoint(this.#config);
    return buildActorGatewayUrl(endpoint, actorId, this.#config.token);
  }
  async proxyRequest(_c, actorRequest, actorId) {
    if (this.#metadataPromise) {
      await this.#metadataPromise;
    }
    return await sendHttpRequestToActor(
      this.#config,
      actorId,
      actorRequest
    );
  }
  async proxyWebSocket(c, path, actorId, encoding, params) {
    var _a, _b;
    if (this.#metadataPromise) {
      await this.#metadataPromise;
    }
    const upgradeWebSocket = (_b = (_a = this.#config).getUpgradeWebSocket) == null ? void 0 : _b.call(_a);
    invariant2(upgradeWebSocket, "missing getUpgradeWebSocket");
    const endpoint = getEndpoint(this.#config);
    const guardUrl = combineUrlPath(endpoint, path);
    const wsGuardUrl = guardUrl.replace("http://", "ws://");
    logger3().debug({
      msg: "forwarding websocket to actor via guard",
      actorId,
      path,
      guardUrl
    });
    const protocols = buildWebSocketProtocols(
      this.#config,
      encoding,
      params
    );
    const args = await createWebSocketProxy(c, wsGuardUrl, protocols);
    return await upgradeWebSocket(() => args)(c, noopNext());
  }
  async kvGet(actorId, key) {
    if (this.#metadataPromise) {
      await this.#metadataPromise;
    }
    logger3().debug({ msg: "getting kv value via engine api", key });
    const response = await kvGet(
      this.#config,
      actorId,
      new TextDecoder("utf8").decode(key)
    );
    return response.value;
  }
  displayInformation() {
    return { properties: {} };
  }
  setGetUpgradeWebSocket(getUpgradeWebSocket) {
    this.#config.getUpgradeWebSocket = getUpgradeWebSocket;
  }
};
function apiActorToOutput(actor) {
  return {
    actorId: actor.actor_id,
    name: actor.name,
    key: deserializeActorKey(actor.key),
    createTs: actor.create_ts,
    startTs: actor.start_ts ?? null,
    connectableTs: actor.connectable_ts ?? null,
    sleepTs: actor.sleep_ts ?? null,
    destroyTs: actor.destroy_ts ?? null,
    error: actor.error ?? void 0
  };
}

// src/client/actor-conn.ts
import * as cbor4 from "cbor-x";
import invariant3 from "invariant";
import pRetry2 from "p-retry";

// src/client/actor-query.ts
async function queryActor(c, query, managerDriver) {
  logger().debug({ msg: "querying actor", query: JSON.stringify(query) });
  let actorOutput;
  if ("getForId" in query) {
    const output = await managerDriver.getForId({
      c,
      name: query.getForId.name,
      actorId: query.getForId.actorId
    });
    if (!output) throw new ActorNotFound(query.getForId.actorId);
    actorOutput = output;
  } else if ("getForKey" in query) {
    const existingActor = await managerDriver.getWithKey({
      c,
      name: query.getForKey.name,
      key: query.getForKey.key
    });
    if (!existingActor) {
      throw new ActorNotFound(
        `${query.getForKey.name}:${JSON.stringify(query.getForKey.key)}`
      );
    }
    actorOutput = existingActor;
  } else if ("getOrCreateForKey" in query) {
    const getOrCreateOutput = await managerDriver.getOrCreateWithKey({
      c,
      name: query.getOrCreateForKey.name,
      key: query.getOrCreateForKey.key,
      input: query.getOrCreateForKey.input,
      region: query.getOrCreateForKey.region
    });
    actorOutput = {
      actorId: getOrCreateOutput.actorId
    };
  } else if ("create" in query) {
    const createOutput = await managerDriver.createActor({
      c,
      name: query.create.name,
      key: query.create.key,
      input: query.create.input,
      region: query.create.region
    });
    actorOutput = {
      actorId: createOutput.actorId
    };
  } else {
    throw new InvalidRequest("Invalid query format");
  }
  logger().debug({ msg: "actor query result", actorId: actorOutput.actorId });
  return { actorId: actorOutput.actorId };
}
function getActorNameFromQuery(query) {
  if ("getForId" in query) return query.getForId.name;
  if ("getForKey" in query) return query.getForKey.name;
  if ("getOrCreateForKey" in query) return query.getOrCreateForKey.name;
  if ("create" in query) return query.create.name;
  throw new InvalidRequest("Invalid query format");
}
async function checkForSchedulingError(group, code, actorId, query, driver) {
  const name = getActorNameFromQuery(query);
  try {
    const actor = await driver.getForId({ name, actorId });
    if (actor == null ? void 0 : actor.error) {
      logger().info({
        msg: "found actor scheduling error",
        actorId,
        error: actor.error
      });
      return new ActorSchedulingError(group, code, actorId, actor.error);
    }
  } catch (err) {
    logger().warn({
      msg: "failed to fetch actor details for scheduling error check",
      actorId,
      error: stringifyError(err)
    });
  }
  return null;
}

// src/client/queue.ts
import * as cbor3 from "cbor-x";
function createQueueSender(senderOptions) {
  return {
    async send(name, body, options) {
      const normalizedOptions = options instanceof AbortSignal ? { signal: options } : options;
      const wait = (normalizedOptions == null ? void 0 : normalizedOptions.wait) ?? false;
      const timeout = normalizedOptions == null ? void 0 : normalizedOptions.timeout;
      const result = await sendHttpRequest({
        url: `http://actor/queue/${encodeURIComponent(name)}`,
        method: "POST",
        headers: {
          [HEADER_ENCODING]: senderOptions.encoding,
          ...senderOptions.params !== void 0 ? {
            [HEADER_CONN_PARAMS]: JSON.stringify(
              senderOptions.params
            )
          } : {}
        },
        body: { body, wait, timeout },
        encoding: senderOptions.encoding,
        customFetch: senderOptions.customFetch,
        signal: normalizedOptions == null ? void 0 : normalizedOptions.signal,
        requestVersion: CURRENT_VERSION,
        requestVersionedDataHandler: HTTP_QUEUE_SEND_REQUEST_VERSIONED,
        responseVersion: CURRENT_VERSION,
        responseVersionedDataHandler: HTTP_QUEUE_SEND_RESPONSE_VERSIONED,
        requestZodSchema: HttpQueueSendRequestSchema,
        responseZodSchema: HttpQueueSendResponseSchema,
        requestToJson: (value) => ({
          ...value,
          name
        }),
        requestToBare: (value) => ({
          name: value.name ?? name,
          body: bufferToArrayBuffer(cbor3.encode(value.body)),
          wait: value.wait ?? false,
          timeout: value.timeout !== void 0 ? BigInt(value.timeout) : null
        }),
        responseFromJson: (json) => {
          if (json.response === void 0) {
            return { status: json.status };
          }
          return {
            status: json.status,
            response: json.response
          };
        },
        responseFromBare: (bare) => {
          if (bare.response === null || bare.response === void 0) {
            return { status: bare.status };
          }
          return {
            status: bare.status,
            response: cbor3.decode(new Uint8Array(bare.response))
          };
        }
      });
      if (wait) {
        return result;
      }
      return;
    }
  };
}
function createQueueProxy(sender) {
  const methodCache = /* @__PURE__ */ new Map();
  return new Proxy(sender, {
    get(target, prop, receiver) {
      if (typeof prop === "symbol") {
        return Reflect.get(target, prop, receiver);
      }
      if (prop in target) {
        const value = Reflect.get(target, prop, target);
        if (typeof value === "function") {
          return value.bind(target);
        }
        return value;
      }
      if (prop === "then") return void 0;
      if (typeof prop === "string") {
        let method = methodCache.get(prop);
        if (!method) {
          method = {
            send: (body, options) => target.send(prop, body, options)
          };
          methodCache.set(prop, method);
        }
        return method;
      }
    },
    has(target, prop) {
      if (typeof prop === "string") {
        return true;
      }
      return Reflect.has(target, prop);
    }
  });
}

// src/client/actor-conn.ts
var CONNECT_SYMBOL = Symbol("connect");
var ActorConnRaw = class {
  #disposed = false;
  /* Will be aborted on dispose. */
  #abortController = new AbortController();
  #connStatus = "idle";
  #actorId;
  #connId;
  #messageQueue = [];
  #actionsInFlight = /* @__PURE__ */ new Map();
  // biome-ignore lint/suspicious/noExplicitAny: Unknown subscription type
  #eventSubscriptions = /* @__PURE__ */ new Map();
  #errorHandlers = /* @__PURE__ */ new Set();
  #openHandlers = /* @__PURE__ */ new Set();
  #closeHandlers = /* @__PURE__ */ new Set();
  #statusChangeHandlers = /* @__PURE__ */ new Set();
  #actionIdCounter = 0;
  #queueSender;
  /**
   * Interval that keeps the NodeJS process alive if this is the only thing running.
   *
   * See ttps://github.com/nodejs/node/issues/22088
   */
  #keepNodeAliveInterval;
  /** Promise used to indicate the socket has connected successfully. This will be rejected if the connection fails. */
  #onOpenPromise;
  #websocket;
  #client;
  #driver;
  #params;
  #encoding;
  #actorQuery;
  // TODO: ws message queue
  /**
   * Do not call this directly.
   *
   * Creates an instance of ActorConnRaw.
   *
   * @protected
   */
  constructor(client, driver, params, encoding, actorQuery) {
    this.#client = client;
    this.#driver = driver;
    this.#params = params;
    this.#encoding = encoding;
    this.#actorQuery = actorQuery;
    this.#queueSender = createQueueSender({
      encoding: this.#encoding,
      params: this.#params,
      customFetch: async (request) => {
        if (!this.#actorId) {
          const { actorId } = await queryActor(
            void 0,
            this.#actorQuery,
            this.#driver
          );
          this.#actorId = actorId;
        }
        return this.#driver.sendRequest(this.#actorId, request);
      }
    });
    this.#keepNodeAliveInterval = setInterval(() => 6e4);
  }
  get queue() {
    return createQueueProxy(this.#queueSender);
  }
  /**
   * Call a raw action connection. See {@link ActorConn} for type-safe action calls.
   *
   * @see {@link ActorConn}
   * @template Args - The type of arguments to pass to the action function.
   * @template Response - The type of the response returned by the action function.
   * @param {string} name - The name of the action function to call.
   * @param {...Args} args - The arguments to pass to the action function.
   * @returns {Promise<Response>} - A promise that resolves to the response of the action function.
   */
  async action(opts) {
    logger().debug({ msg: "action", name: opts.name, args: opts.args });
    const actionId = this.#actionIdCounter;
    this.#actionIdCounter += 1;
    const { promise, resolve, reject } = promiseWithResolvers();
    this.#actionsInFlight.set(actionId, {
      name: opts.name,
      resolve,
      reject
    });
    logger().debug({
      msg: "added action to in-flight map",
      actionId,
      actionName: opts.name,
      inFlightCount: this.#actionsInFlight.size
    });
    this.#sendMessage({
      body: {
        tag: "ActionRequest",
        val: {
          id: BigInt(actionId),
          name: opts.name,
          args: opts.args
        }
      }
    });
    const { id: responseId, output } = await promise;
    if (responseId !== BigInt(actionId))
      throw new Error(
        `Request ID ${actionId} does not match response ID ${responseId}`
      );
    return output;
  }
  /**
   * Do not call this directly.
   * Establishes a connection to the server using the specified endpoint & encoding & driver.
   *
   * @protected
   */
  [CONNECT_SYMBOL]() {
    this.#connectWithRetry();
  }
  #setConnStatus(status) {
    const prevStatus = this.#connStatus;
    if (prevStatus === status) return;
    this.#connStatus = status;
    for (const handler of [...this.#statusChangeHandlers]) {
      try {
        handler(status);
      } catch (err) {
        logger().error({
          msg: "error in status change handler",
          error: stringifyError(err)
        });
      }
    }
    if (status === "connected") {
      for (const handler of [...this.#openHandlers]) {
        try {
          handler();
        } catch (err) {
          logger().error({
            msg: "error in open handler",
            error: stringifyError(err)
          });
        }
      }
    }
    if ((status === "disconnected" || status === "idle") && prevStatus === "connected") {
      for (const handler of [...this.#closeHandlers]) {
        try {
          handler();
        } catch (err) {
          logger().error({
            msg: "error in close handler",
            error: stringifyError(err)
          });
        }
      }
    }
  }
  #connectWithRetry() {
    this.#setConnStatus("connecting");
    pRetry2(this.#connectAndWait.bind(this), {
      forever: true,
      minTimeout: 250,
      maxTimeout: 3e4,
      onFailedAttempt: (error) => {
        logger().warn({
          msg: "failed to reconnect",
          attempt: error.attemptNumber,
          error: stringifyError(error)
        });
      },
      // Cancel retry if aborted
      signal: this.#abortController.signal
    }).catch((err) => {
      if (err.name === "AbortError") {
        logger().info({ msg: "connection retry aborted" });
      } else {
        logger().error({
          msg: "unexpected error in connection retry",
          error: stringifyError(err)
        });
      }
    });
  }
  async #connectAndWait() {
    try {
      if (this.#onOpenPromise)
        throw new Error("#onOpenPromise already defined");
      this.#onOpenPromise = promiseWithResolvers();
      await this.#connectWebSocket();
      await this.#onOpenPromise.promise;
    } finally {
      this.#onOpenPromise = void 0;
    }
  }
  async #connectWebSocket() {
    const { actorId } = await queryActor(
      void 0,
      this.#actorQuery,
      this.#driver
    );
    this.#actorId = actorId;
    const ws = await this.#driver.openWebSocket(
      PATH_CONNECT,
      actorId,
      this.#encoding,
      this.#params
    );
    logger().debug({
      msg: "opened websocket",
      connId: this.#connId,
      readyState: ws.readyState,
      messageQueueLength: this.#messageQueue.length
    });
    this.#websocket = ws;
    ws.addEventListener("open", () => {
      logger().debug({
        msg: "client websocket open",
        connId: this.#connId
      });
    });
    ws.addEventListener("message", async (ev) => {
      try {
        await this.#handleOnMessage(ev.data);
      } catch (err) {
        logger().error({
          msg: "error in websocket message handler",
          error: stringifyError(err)
        });
      }
    });
    ws.addEventListener("close", async (ev) => {
      try {
        await this.#handleOnClose(ev);
      } catch (err) {
        logger().error({
          msg: "error in websocket close handler",
          error: stringifyError(err)
        });
      }
    });
    ws.addEventListener("error", (_ev) => {
      try {
        this.#handleOnError();
      } catch (err) {
        logger().error({
          msg: "error in websocket error handler",
          error: stringifyError(err)
        });
      }
    });
  }
  /** Called by the onopen event from drivers. */
  #handleOnOpen() {
    if (this.#disposed) {
      logger().debug({
        msg: "handleOnOpen called after dispose, closing websocket"
      });
      if (this.#websocket) {
        this.#websocket.close(1e3, "Disposed");
        this.#websocket = void 0;
      }
      return;
    }
    logger().debug({
      msg: "socket open",
      messageQueueLength: this.#messageQueue.length,
      connId: this.#connId
    });
    this.#setConnStatus("connected");
    if (this.#onOpenPromise) {
      this.#onOpenPromise.resolve(void 0);
    } else {
      logger().warn({ msg: "#onOpenPromise is undefined" });
    }
    for (const eventName of this.#eventSubscriptions.keys()) {
      this.#sendSubscription(eventName, true);
    }
    const queue = this.#messageQueue;
    this.#messageQueue = [];
    logger().debug({
      msg: "flushing message queue",
      queueLength: queue.length
    });
    for (const msg of queue) {
      this.#sendMessage(msg);
    }
  }
  /** Called by the onmessage event from drivers. */
  async #handleOnMessage(data) {
    logger().trace({
      msg: "received message",
      dataType: typeof data,
      isBlob: data instanceof Blob,
      isArrayBuffer: data instanceof ArrayBuffer
    });
    const response = await this.#parseMessage(data);
    logger().trace(
      getLogMessage() ? {
        msg: "parsed message",
        message: jsonStringifyCompat(response).substring(0, 100) + "..."
      } : { msg: "parsed message" }
    );
    if (response.body.tag === "Init") {
      this.#actorId = response.body.val.actorId;
      this.#connId = response.body.val.connectionId;
      logger().trace({
        msg: "received init message",
        actorId: this.#actorId,
        connId: this.#connId
      });
      this.#handleOnOpen();
    } else if (response.body.tag === "Error") {
      const { group, code, message, metadata, actionId } = response.body.val;
      if (actionId) {
        const inFlight = this.#takeActionInFlight(Number(actionId));
        logger().warn({
          msg: "action error",
          actionId,
          actionName: inFlight == null ? void 0 : inFlight.name,
          group,
          code,
          message,
          metadata
        });
        inFlight.reject(
          new ActorError(group, code, message, metadata)
        );
      } else {
        logger().warn({
          msg: "connection error",
          group,
          code,
          message,
          metadata
        });
        let errorToThrow = new ActorError(
          group,
          code,
          message,
          metadata
        );
        if (isSchedulingError(group, code) && this.#actorId) {
          const schedulingError = await checkForSchedulingError(
            group,
            code,
            this.#actorId,
            this.#actorQuery,
            this.#driver
          );
          if (schedulingError) {
            errorToThrow = schedulingError;
          }
        }
        if (this.#onOpenPromise) {
          this.#onOpenPromise.reject(errorToThrow);
        }
        for (const [id, inFlight] of this.#actionsInFlight.entries()) {
          inFlight.reject(errorToThrow);
          this.#actionsInFlight.delete(id);
        }
        this.#dispatchActorError(errorToThrow);
      }
    } else if (response.body.tag === "ActionResponse") {
      const { id: actionId } = response.body.val;
      logger().debug({
        msg: "received action response",
        actionId: Number(actionId),
        inFlightCount: this.#actionsInFlight.size,
        inFlightIds: Array.from(this.#actionsInFlight.keys())
      });
      const inFlight = this.#takeActionInFlight(Number(actionId));
      logger().trace({
        msg: "resolving action promise",
        actionId,
        actionName: inFlight == null ? void 0 : inFlight.name
      });
      inFlight.resolve(response.body.val);
    } else if (response.body.tag === "Event") {
      logger().trace({
        msg: "received event",
        name: response.body.val.name
      });
      this.#dispatchEvent(response.body.val);
    } else {
      assertUnreachable(response.body);
    }
  }
  /** Called by the onclose event from drivers. */
  async #handleOnClose(event) {
    const closeEvent = event;
    const wasClean = closeEvent.wasClean;
    const wasConnected = this.#connStatus === "connected";
    logger().info({
      msg: "socket closed",
      code: closeEvent.code,
      reason: closeEvent.reason,
      wasClean,
      disposed: this.#disposed,
      connId: this.#connId
    });
    this.#websocket = void 0;
    if (this.#disposed) {
      this.#rejectPendingPromises(new ActorConnDisposed(), true);
    } else {
      this.#setConnStatus("disconnected");
      let error;
      const reason = closeEvent.reason || "";
      const parsed = parseWebSocketCloseReason(reason);
      if (parsed) {
        const { group, code } = parsed;
        if (isSchedulingError(group, code) && this.#actorId) {
          const schedulingError = await checkForSchedulingError(
            group,
            code,
            this.#actorId,
            this.#actorQuery,
            this.#driver
          );
          if (schedulingError) {
            error = schedulingError;
          } else {
            error = new ActorError(
              group,
              code,
              `Connection closed: ${reason}`,
              void 0
            );
          }
        } else {
          error = new ActorError(
            group,
            code,
            `Connection closed: ${reason}`,
            void 0
          );
        }
      } else {
        error = new Error(
          `${wasClean ? "Connection closed" : "Connection lost"} (code: ${closeEvent.code}, reason: ${reason})`
        );
      }
      this.#rejectPendingPromises(error, false);
      if (error instanceof ActorError) {
        this.#dispatchActorError(error);
      }
      if (wasConnected) {
        logger().debug({
          msg: "triggering reconnect",
          connId: this.#connId
        });
        this.#connectWithRetry();
      }
    }
  }
  #rejectPendingPromises(error, suppressUnhandled) {
    if (this.#onOpenPromise) {
      if (suppressUnhandled) {
        this.#onOpenPromise.promise.catch(() => {
        });
      }
      this.#onOpenPromise.reject(error);
    }
    for (const actionInfo of this.#actionsInFlight.values()) {
      actionInfo.reject(error);
    }
    this.#actionsInFlight.clear();
  }
  /** Called by the onerror event from drivers. */
  #handleOnError() {
    if (this.#disposed) return;
    logger().warn("socket error");
  }
  #takeActionInFlight(id) {
    const inFlight = this.#actionsInFlight.get(id);
    if (!inFlight) {
      logger().error({
        msg: "action not found in in-flight map",
        lookupId: id,
        inFlightCount: this.#actionsInFlight.size,
        inFlightIds: Array.from(this.#actionsInFlight.keys()),
        inFlightActions: Array.from(
          this.#actionsInFlight.entries()
        ).map(([id2, action]) => ({
          id: id2,
          name: action.name
        }))
      });
      throw new InternalError2(`No in flight response for ${id}`);
    }
    this.#actionsInFlight.delete(id);
    logger().debug({
      msg: "removed action from in-flight map",
      actionId: id,
      actionName: inFlight.name,
      inFlightCount: this.#actionsInFlight.size
    });
    return inFlight;
  }
  #dispatchEvent(event) {
    const { name, args } = event;
    const listeners = this.#eventSubscriptions.get(name);
    if (!listeners) return;
    for (const listener of [...listeners]) {
      listener.callback(...args);
      if (listener.once) {
        listeners.delete(listener);
      }
    }
    if (listeners.size === 0) {
      this.#eventSubscriptions.delete(name);
    }
  }
  #dispatchActorError(error) {
    for (const handler of [...this.#errorHandlers]) {
      try {
        handler(error);
      } catch (err) {
        logger().error({
          msg: "error in connection error handler",
          error: stringifyError(err)
        });
      }
    }
  }
  #addEventSubscription(eventName, callback, once) {
    const listener = {
      callback,
      once
    };
    let subscriptionSet = this.#eventSubscriptions.get(eventName);
    if (subscriptionSet === void 0) {
      subscriptionSet = /* @__PURE__ */ new Set();
      this.#eventSubscriptions.set(eventName, subscriptionSet);
      this.#sendSubscription(eventName, true);
    }
    subscriptionSet.add(listener);
    return () => {
      const listeners = this.#eventSubscriptions.get(eventName);
      if (listeners) {
        listeners.delete(listener);
        if (listeners.size === 0) {
          this.#eventSubscriptions.delete(eventName);
          this.#sendSubscription(eventName, false);
        }
      }
    };
  }
  /**
   * Subscribes to an event that will happen repeatedly.
   *
   * @template Args - The type of arguments the event callback will receive.
   * @param {string} eventName - The name of the event to subscribe to.
   * @param {(...args: Args) => void} callback - The callback function to execute when the event is triggered.
   * @returns {EventUnsubscribe} - A function to unsubscribe from the event.
   * @see {@link https://rivet.dev/docs/events|Events Documentation}
   */
  on(eventName, callback) {
    return this.#addEventSubscription(eventName, callback, false);
  }
  /**
   * Subscribes to an event that will be triggered only once.
   *
   * @template Args - The type of arguments the event callback will receive.
   * @param {string} eventName - The name of the event to subscribe to.
   * @param {(...args: Args) => void} callback - The callback function to execute when the event is triggered.
   * @returns {EventUnsubscribe} - A function to unsubscribe from the event.
   * @see {@link https://rivet.dev/docs/events|Events Documentation}
   */
  once(eventName, callback) {
    return this.#addEventSubscription(eventName, callback, true);
  }
  /**
   * Subscribes to connection errors.
   *
   * @param {ActorErrorCallback} callback - The callback function to execute when a connection error occurs.
   * @returns {() => void} - A function to unsubscribe from the error handler.
   */
  onError(callback) {
    this.#errorHandlers.add(callback);
    return () => {
      this.#errorHandlers.delete(callback);
    };
  }
  /**
   * Returns the current connection status.
   *
   * @returns {ActorConnStatus} - The current connection status.
   */
  get connStatus() {
    return this.#connStatus;
  }
  /**
   * Returns whether the connection is currently open.
   *
   * @deprecated Use `connStatus` instead.
   * @returns {boolean} - True if the connection is open, false otherwise.
   */
  get isConnected() {
    return this.#connStatus === "connected";
  }
  /**
   * Subscribes to connection open events.
   *
   * This is called when the WebSocket connection is established and the Init message is received.
   *
   * @param {ConnectionStateCallback} callback - The callback function to execute when the connection opens.
   * @returns {() => void} - A function to unsubscribe from the open handler.
   */
  onOpen(callback) {
    this.#openHandlers.add(callback);
    return () => {
      this.#openHandlers.delete(callback);
    };
  }
  /**
   * Subscribes to connection close events.
   *
   * This is called when the WebSocket connection is closed. The connection will automatically
   * attempt to reconnect unless disposed.
   *
   * @param {ConnectionStateCallback} callback - The callback function to execute when the connection closes.
   * @returns {() => void} - A function to unsubscribe from the close handler.
   */
  onClose(callback) {
    this.#closeHandlers.add(callback);
    return () => {
      this.#closeHandlers.delete(callback);
    };
  }
  /**
   * Subscribes to connection status changes.
   *
   * This is called whenever the connection status changes between Disconnected, Connecting, and Connected.
   *
   * @param {StatusChangeCallback} callback - The callback function to execute when the status changes.
   * @returns {() => void} - A function to unsubscribe from the status change handler.
   */
  onStatusChange(callback) {
    this.#statusChangeHandlers.add(callback);
    return () => {
      this.#statusChangeHandlers.delete(callback);
    };
  }
  #sendMessage(message, opts) {
    var _a, _b;
    if (this.#disposed) {
      if (opts == null ? void 0 : opts.ephemeral) {
        return;
      } else {
        throw new ActorConnDisposed();
      }
    }
    let queueMessage = false;
    if (this.#websocket) {
      const readyState = this.#websocket.readyState;
      logger().debug({
        msg: "websocket send attempt",
        readyState,
        readyStateString: readyState === 0 ? "CONNECTING" : readyState === 1 ? "OPEN" : readyState === 2 ? "CLOSING" : "CLOSED",
        connId: this.#connId,
        messageType: message.body.tag,
        actionName: (_a = message.body.val) == null ? void 0 : _a.name
      });
      if (readyState === 1) {
        try {
          const messageSerialized = serializeWithEncoding(
            this.#encoding,
            message,
            TO_SERVER_VERSIONED,
            CURRENT_VERSION,
            ToServerSchema,
            // JSON: args is the raw value
            (msg) => msg,
            // BARE: args needs to be CBOR-encoded to ArrayBuffer
            (msg) => {
              if (msg.body.tag === "ActionRequest") {
                return {
                  body: {
                    tag: "ActionRequest",
                    val: {
                      id: msg.body.val.id,
                      name: msg.body.val.name,
                      args: bufferToArrayBuffer(
                        cbor4.encode(msg.body.val.args)
                      )
                    }
                  }
                };
              } else {
                return msg;
              }
            }
          );
          this.#websocket.send(messageSerialized);
          logger().trace({
            msg: "sent websocket message",
            len: messageLength(messageSerialized)
          });
        } catch (error) {
          logger().warn({
            msg: "failed to send message, added to queue",
            error,
            connId: this.#connId
          });
          queueMessage = true;
        }
      } else {
        logger().debug({
          msg: "websocket not open, queueing message",
          readyState
        });
        queueMessage = true;
      }
    } else {
      logger().debug({ msg: "no websocket, queueing message" });
      queueMessage = true;
    }
    if (!(opts == null ? void 0 : opts.ephemeral) && queueMessage) {
      this.#messageQueue.push(message);
      logger().debug({
        msg: "queued connection message",
        queueLength: this.#messageQueue.length,
        connId: this.#connId,
        messageType: message.body.tag,
        actionName: (_b = message.body.val) == null ? void 0 : _b.name
      });
    }
  }
  async #parseMessage(data) {
    invariant3(this.#websocket, "websocket must be defined");
    const buffer = await inputDataToBuffer(data);
    return deserializeWithEncoding(
      this.#encoding,
      buffer,
      TO_CLIENT_VERSIONED,
      ToClientSchema,
      // JSON: values are already the correct type
      (msg) => msg,
      // BARE: need to decode ArrayBuffer fields back to unknown
      (msg) => {
        if (msg.body.tag === "Error") {
          return {
            body: {
              tag: "Error",
              val: {
                group: msg.body.val.group,
                code: msg.body.val.code,
                message: msg.body.val.message,
                metadata: msg.body.val.metadata ? cbor4.decode(
                  new Uint8Array(
                    msg.body.val.metadata
                  )
                ) : null,
                actionId: msg.body.val.actionId
              }
            }
          };
        } else if (msg.body.tag === "ActionResponse") {
          return {
            body: {
              tag: "ActionResponse",
              val: {
                id: msg.body.val.id,
                output: cbor4.decode(
                  new Uint8Array(msg.body.val.output)
                )
              }
            }
          };
        } else if (msg.body.tag === "Event") {
          return {
            body: {
              tag: "Event",
              val: {
                name: msg.body.val.name,
                args: cbor4.decode(
                  new Uint8Array(msg.body.val.args)
                )
              }
            }
          };
        } else {
          return msg;
        }
      }
    );
  }
  /**
   * Get the actor ID (for testing purposes).
   * @internal
   */
  get actorId() {
    return this.#actorId;
  }
  /**
   * Get the connection ID (for testing purposes).
   * @internal
   */
  get connId() {
    return this.#connId;
  }
  /**
   * Get the connection ID (for testing purposes).
   * @internal
   * @deprecated Use `connId` instead.
   */
  get connectionId() {
    return this.#connId;
  }
  /**
   * Disconnects from the actor.
   *
   * @returns {Promise<void>} A promise that resolves when the socket is gracefully closed.
   */
  async dispose() {
    if (this.#disposed) {
      logger().warn({ msg: "connection already disconnected" });
      return;
    }
    this.#disposed = true;
    logger().debug({ msg: "disposing actor conn" });
    this.#setConnStatus("idle");
    clearInterval(this.#keepNodeAliveInterval);
    this.#abortController.abort();
    this.#client[ACTOR_CONNS_SYMBOL].delete(this);
    if (this.#websocket) {
      const ws = this.#websocket;
      if (ws.readyState !== 2 && ws.readyState !== 3) {
        const { promise, resolve } = promiseWithResolvers();
        ws.addEventListener("close", () => resolve(void 0));
        ws.close(1e3, "Disposed");
        await promise;
      }
    } else {
      this.#rejectPendingPromises(new ActorConnDisposed(), true);
    }
    this.#websocket = void 0;
  }
  #sendSubscription(eventName, subscribe) {
    this.#sendMessage(
      {
        body: {
          tag: "SubscriptionRequest",
          val: {
            eventName,
            subscribe
          }
        }
      },
      { ephemeral: true }
    );
  }
};

// src/client/actor-handle.ts
import * as cbor5 from "cbor-x";
import invariant5 from "invariant";

// src/client/raw-utils.ts
import invariant4 from "invariant";
async function rawHttpFetch(driver, actorQuery, params, input, init) {
  let path;
  let mergedInit = init || {};
  if (typeof input === "string") {
    path = input;
  } else if (input instanceof URL) {
    path = input.pathname + input.search;
  } else if (input instanceof Request) {
    const url = new URL(input.url);
    path = url.pathname + url.search;
    const requestHeaders = new Headers(input.headers);
    const initHeaders = new Headers((init == null ? void 0 : init.headers) || {});
    const mergedHeaders = new Headers(requestHeaders);
    initHeaders.forEach((value, key) => {
      mergedHeaders.set(key, value);
    });
    mergedInit = {
      method: input.method,
      body: input.body,
      mode: input.mode,
      credentials: input.credentials,
      redirect: input.redirect,
      referrer: input.referrer,
      referrerPolicy: input.referrerPolicy,
      integrity: input.integrity,
      keepalive: input.keepalive,
      signal: input.signal,
      ...mergedInit,
      // init overrides Request properties
      headers: mergedHeaders
      // headers must be set after spread to ensure proper merge
    };
    if (mergedInit.body) {
      mergedInit.duplex = "half";
    }
  } else {
    throw new TypeError("Invalid input type for fetch");
  }
  try {
    const { actorId } = await queryActor(void 0, actorQuery, driver);
    logger().debug({ msg: "found actor for raw http", actorId });
    invariant4(actorId, "Missing actor ID");
    const normalizedPath = path.startsWith("/") ? path.slice(1) : path;
    const url = new URL(`http://actor/request/${normalizedPath}`);
    const proxyRequestHeaders = new Headers(mergedInit.headers);
    if (params) {
      proxyRequestHeaders.set(HEADER_CONN_PARAMS, JSON.stringify(params));
    }
    const proxyRequest = new Request(url, {
      ...mergedInit,
      headers: proxyRequestHeaders
    });
    return driver.sendRequest(actorId, proxyRequest);
  } catch (err) {
    const { group, code, message, metadata } = deconstructError(
      err,
      logger(),
      {},
      true
    );
    throw new ActorError(group, code, message, metadata);
  }
}
async function rawWebSocket(driver, actorQuery, params, path, protocols) {
  const encoding = "bare";
  const { actorId } = await queryActor(void 0, actorQuery, driver);
  logger().debug({ msg: "found actor for action", actorId });
  invariant4(actorId, "Missing actor ID");
  let pathPortion = "";
  let queryPortion = "";
  if (path) {
    const queryIndex = path.indexOf("?");
    if (queryIndex !== -1) {
      pathPortion = path.substring(0, queryIndex);
      queryPortion = path.substring(queryIndex);
    } else {
      pathPortion = path;
    }
    if (pathPortion.startsWith("/")) {
      pathPortion = pathPortion.slice(1);
    }
  }
  const fullPath = `${PATH_WEBSOCKET_PREFIX}${pathPortion}${queryPortion}`;
  logger().debug({
    msg: "opening websocket",
    actorId,
    encoding,
    path: fullPath
  });
  const ws = await driver.openWebSocket(fullPath, actorId, encoding, params);
  return ws;
}

// src/client/actor-handle.ts
var ActorHandleRaw = class {
  #client;
  #driver;
  #encoding;
  #actorQuery;
  #params;
  #queueSender;
  /**
   * Do not call this directly.
   *
   * Creates an instance of ActorHandleRaw.
   *
   * @protected
   */
  constructor(client, driver, params, encoding, actorQuery) {
    this.#client = client;
    this.#driver = driver;
    this.#encoding = encoding;
    this.#actorQuery = actorQuery;
    this.#params = params;
    this.#queueSender = createQueueSender({
      encoding: this.#encoding,
      params: this.#params,
      customFetch: async (request) => {
        const { actorId } = await queryActor(
          void 0,
          this.#actorQuery,
          this.#driver
        );
        return this.#driver.sendRequest(actorId, request);
      }
    });
  }
  get queue() {
    return createQueueProxy(this.#queueSender);
  }
  /**
   * Call a raw action. This method sends an HTTP request to invoke the named action.
   *
   * @see {@link ActorHandle}
   * @template Args - The type of arguments to pass to the action function.
   * @template Response - The type of the response returned by the action function.
   */
  async action(opts) {
    let actorId;
    try {
      const result = await queryActor(
        void 0,
        this.#actorQuery,
        this.#driver
      );
      actorId = result.actorId;
      logger().debug({ msg: "found actor for action", actorId });
      invariant5(actorId, "Missing actor ID");
      logger().debug({
        msg: "handling action",
        name: opts.name,
        encoding: this.#encoding
      });
      const responseData = await sendHttpRequest({
        url: `http://actor/action/${encodeURIComponent(opts.name)}`,
        method: "POST",
        headers: {
          [HEADER_ENCODING]: this.#encoding,
          ...this.#params !== void 0 ? { [HEADER_CONN_PARAMS]: JSON.stringify(this.#params) } : {}
        },
        body: opts.args,
        encoding: this.#encoding,
        customFetch: this.#driver.sendRequest.bind(
          this.#driver,
          actorId
        ),
        signal: opts == null ? void 0 : opts.signal,
        requestVersion: CURRENT_VERSION,
        requestVersionedDataHandler: HTTP_ACTION_REQUEST_VERSIONED,
        responseVersion: CURRENT_VERSION,
        responseVersionedDataHandler: HTTP_ACTION_RESPONSE_VERSIONED,
        requestZodSchema: HttpActionRequestSchema,
        responseZodSchema: HttpActionResponseSchema,
        // JSON Request: args is the raw value
        requestToJson: (args) => ({
          args
        }),
        // BARE Request: args needs to be CBOR-encoded
        requestToBare: (args) => ({
          args: bufferToArrayBuffer(cbor5.encode(args))
        }),
        // JSON Response: output is the raw value
        responseFromJson: (json) => json.output,
        // BARE Response: output is ArrayBuffer that needs CBOR-decoding
        responseFromBare: (bare) => cbor5.decode(new Uint8Array(bare.output))
      });
      return responseData;
    } catch (err) {
      const { group, code, message, metadata } = deconstructError(
        err,
        logger(),
        {},
        true
      );
      if (actorId && isSchedulingError(group, code)) {
        const schedulingError = await checkForSchedulingError(
          group,
          code,
          actorId,
          this.#actorQuery,
          this.#driver
        );
        if (schedulingError) {
          throw schedulingError;
        }
      }
      throw new ActorError(group, code, message, metadata);
    }
  }
  /**
   * Establishes a persistent connection to the actor.
   *
   * @template AD The actor class that this connection is for.
   * @returns {ActorConn<AD>} A connection to the actor.
   */
  connect() {
    logger().debug({
      msg: "establishing connection from handle",
      query: this.#actorQuery
    });
    const conn = new ActorConnRaw(
      this.#client,
      this.#driver,
      this.#params,
      this.#encoding,
      this.#actorQuery
    );
    return this.#client[CREATE_ACTOR_CONN_PROXY](
      conn
    );
  }
  /**
   * Fetches a resource from this actor via the /request endpoint. This is a
   * convenience wrapper around the raw HTTP API.
   */
  fetch(input, init) {
    return rawHttpFetch(
      this.#driver,
      this.#actorQuery,
      this.#params,
      input,
      init
    );
  }
  /**
   * Opens a raw WebSocket connection to this actor.
   */
  webSocket(path, protocols) {
    return rawWebSocket(
      this.#driver,
      this.#actorQuery,
      this.#params,
      path,
      protocols
    );
  }
  /**
   * Resolves the actor to get its unique actor ID.
   */
  async resolve() {
    if ("getForKey" in this.#actorQuery) {
      const name = this.#actorQuery.getForKey.name;
      const { actorId } = await queryActor(
        void 0,
        this.#actorQuery,
        this.#driver
      );
      this.#actorQuery = { getForId: { actorId, name } };
      return actorId;
    } else if ("getOrCreateForKey" in this.#actorQuery) {
      const name = this.#actorQuery.getOrCreateForKey.name;
      const { actorId } = await queryActor(
        void 0,
        this.#actorQuery,
        this.#driver
      );
      this.#actorQuery = { getForId: { actorId, name } };
      return actorId;
    } else if ("getForId" in this.#actorQuery) {
      return this.#actorQuery.getForId.actorId;
    } else if ("create" in this.#actorQuery) {
      invariant5(false, "actorQuery cannot be create");
    } else {
      assertUnreachable2(this.#actorQuery);
    }
  }
  /**
   * Returns the raw URL for routing traffic to the actor.
   */
  async getGatewayUrl() {
    const { actorId } = await queryActor(
      void 0,
      this.#actorQuery,
      this.#driver
    );
    return await this.#driver.buildGatewayUrl(actorId);
  }
};

// src/client/client.ts
var ACTOR_CONNS_SYMBOL = Symbol("actorConns");
var CREATE_ACTOR_CONN_PROXY = Symbol("createActorConnProxy");
var ClientRaw = class {
  #disposed = false;
  [ACTOR_CONNS_SYMBOL] = /* @__PURE__ */ new Set();
  #driver;
  #encodingKind;
  /**
   * Creates an instance of Client.
   */
  constructor(driver, encoding) {
    this.#driver = driver;
    this.#encodingKind = encoding ?? "bare";
  }
  /**
   * Gets a stateless handle to a actor by its ID.
   *
   * @template AD The actor class that this handle is for.
   * @param {string} name - The name of the actor.
   * @param {string} actorId - The ID of the actor.
   * @param {GetWithIdOptions} [opts] - Options for getting the actor.
   * @returns {ActorHandle<AD>} - A handle to the actor.
   */
  getForId(name, actorId, opts) {
    logger().debug({
      msg: "get handle to actor with id",
      name,
      actorId,
      params: opts == null ? void 0 : opts.params
    });
    const actorQuery = {
      getForId: {
        name,
        actorId
      }
    };
    const handle = this.#createHandle(opts == null ? void 0 : opts.params, actorQuery);
    return createActorProxy(handle);
  }
  /**
   * Gets a stateless handle to a actor by its key, but does not create the actor if it doesn't exist.
   *
   * @template AD The actor class that this handle is for.
   * @param {string} name - The name of the actor.
   * @param {string | string[]} [key=[]] - The key to identify the actor. Can be a single string or an array of strings.
   * @param {GetWithIdOptions} [opts] - Options for getting the actor.
   * @returns {ActorHandle<AD>} - A handle to the actor.
   */
  get(name, key, opts) {
    const keyArray = typeof key === "string" ? [key] : key || [];
    logger().debug({
      msg: "get handle to actor",
      name,
      key: keyArray,
      parameters: opts == null ? void 0 : opts.params
    });
    const actorQuery = {
      getForKey: {
        name,
        key: keyArray
      }
    };
    const handle = this.#createHandle(opts == null ? void 0 : opts.params, actorQuery);
    return createActorProxy(handle);
  }
  /**
   * Gets a stateless handle to a actor by its key, creating it if necessary.
   *
   * @template AD The actor class that this handle is for.
   * @param {string} name - The name of the actor.
   * @param {string | string[]} [key=[]] - The key to identify the actor. Can be a single string or an array of strings.
   * @param {GetOptions} [opts] - Options for getting the actor.
   * @returns {ActorHandle<AD>} - A handle to the actor.
   */
  getOrCreate(name, key, opts) {
    const keyArray = typeof key === "string" ? [key] : key || [];
    logger().debug({
      msg: "get or create handle to actor",
      name,
      key: keyArray,
      parameters: opts == null ? void 0 : opts.params,
      createInRegion: opts == null ? void 0 : opts.createInRegion
    });
    const actorQuery = {
      getOrCreateForKey: {
        name,
        key: keyArray,
        input: opts == null ? void 0 : opts.createWithInput,
        region: opts == null ? void 0 : opts.createInRegion
      }
    };
    const handle = this.#createHandle(opts == null ? void 0 : opts.params, actorQuery);
    return createActorProxy(handle);
  }
  /**
   * Creates a new actor with the provided key and returns a stateless handle to it.
   * Resolves the actor ID and returns a handle with getForId query.
   *
   * @template AD The actor class that this handle is for.
   * @param {string} name - The name of the actor.
   * @param {string | string[]} key - The key to identify the actor. Can be a single string or an array of strings.
   * @param {CreateOptions} [opts] - Options for creating the actor (excluding name and key).
   * @returns {Promise<ActorHandle<AD>>} - A promise that resolves to a handle to the actor.
   */
  async create(name, key, opts) {
    const keyArray = typeof key === "string" ? [key] : key || [];
    const createQuery = {
      create: {
        ...opts,
        // Do these last to override `opts`
        name,
        key: keyArray
      }
    };
    logger().debug({
      msg: "create actor handle",
      name,
      key: keyArray,
      parameters: opts == null ? void 0 : opts.params,
      create: createQuery.create
    });
    const { actorId } = await queryActor(
      void 0,
      createQuery,
      this.#driver
    );
    logger().debug({
      msg: "created actor with ID",
      name,
      key: keyArray,
      actorId
    });
    const getForIdQuery = {
      getForId: {
        name,
        actorId
      }
    };
    const handle = this.#createHandle(opts == null ? void 0 : opts.params, getForIdQuery);
    const proxy = createActorProxy(handle);
    return proxy;
  }
  #createHandle(params, actorQuery) {
    return new ActorHandleRaw(
      this,
      this.#driver,
      params,
      this.#encodingKind,
      actorQuery
    );
  }
  [CREATE_ACTOR_CONN_PROXY](conn) {
    this[ACTOR_CONNS_SYMBOL].add(conn);
    conn[CONNECT_SYMBOL]();
    return createActorProxy(conn);
  }
  /**
   * Disconnects from all actors.
   *
   * @returns {Promise<void>} A promise that resolves when all connections are closed.
   */
  async dispose() {
    if (this.#disposed) {
      logger().warn({ msg: "client already disconnected" });
      return;
    }
    this.#disposed = true;
    logger().debug({ msg: "disposing client" });
    const disposePromises = [];
    for (const conn of this[ACTOR_CONNS_SYMBOL].values()) {
      disposePromises.push(conn.dispose());
    }
    await Promise.all(disposePromises);
  }
};
function createClientWithDriver(driver, config = {}) {
  const client = new ClientRaw(driver, config.encoding);
  return new Proxy(client, {
    get: (target, prop, receiver) => {
      if (typeof prop === "symbol" || prop in target) {
        const value = Reflect.get(target, prop, receiver);
        if (typeof value === "function") {
          return value.bind(target);
        }
        return value;
      }
      if (typeof prop === "string") {
        return {
          // Handle methods (stateless action)
          get: (key, opts) => {
            return target.get(prop, key, opts);
          },
          getOrCreate: (key, opts) => {
            return target.getOrCreate(prop, key, opts);
          },
          getForId: (actorId, opts) => {
            return target.getForId(prop, actorId, opts);
          },
          create: async (key, opts = {}) => {
            return await target.create(prop, key, opts);
          }
        };
      }
      return void 0;
    }
  });
}
function createActorProxy(handle) {
  const methodCache = /* @__PURE__ */ new Map();
  return new Proxy(handle, {
    get(target, prop, receiver) {
      if (typeof prop === "symbol") {
        return Reflect.get(target, prop, receiver);
      }
      if (prop === "constructor" || prop in target) {
        const value = Reflect.get(target, prop, target);
        if (typeof value === "function") {
          return value.bind(target);
        }
        return value;
      }
      if (prop === "queue") {
        return Reflect.get(target, prop, receiver);
      }
      if (typeof prop === "string") {
        if (prop === "then" || prop === "queue") return void 0;
        let method = methodCache.get(prop);
        if (!method) {
          method = (...args) => target.action({ name: prop, args });
          methodCache.set(prop, method);
        }
        return method;
      }
    },
    // Support for 'in' operator
    has(target, prop) {
      if (typeof prop === "string") {
        return prop !== "queue";
      }
      return Reflect.has(target, prop);
    },
    // Support instanceof checks
    getPrototypeOf(target) {
      return Reflect.getPrototypeOf(target);
    },
    // Prevent property enumeration of non-existent action methods
    ownKeys(target) {
      return Reflect.ownKeys(target);
    },
    // Support proper property descriptors
    getOwnPropertyDescriptor(target, prop) {
      const targetDescriptor = Reflect.getOwnPropertyDescriptor(
        target,
        prop
      );
      if (targetDescriptor) {
        return targetDescriptor;
      }
      if (typeof prop === "string") {
        if (prop === "queue") {
          return void 0;
        }
        return {
          configurable: true,
          enumerable: false,
          writable: false,
          value: (...args) => target.action({ name: prop, args })
        };
      }
      return void 0;
    }
  });
}

// src/client/config.ts
import z2 from "zod/v4";
function getDefaultEndpoint() {
  var _a;
  if (typeof window !== "undefined" && ((_a = window.location) == null ? void 0 : _a.origin)) {
    return `${window.location.origin}/api/rivet`;
  }
  return "http://127.0.0.1:6420";
}
var ClientConfigSchemaBase = z2.object({
  /**
   * Endpoint to connect to for Rivet Engine or RivetKit manager API.
   *
   * Supports URL auth syntax for namespace and token:
   * - `https://namespace:token@api.rivet.dev`
   * - `https://namespace@api.rivet.dev`
   *
   * Can also be set via RIVET_ENDPOINT environment variables.
   *
   * Defaults to current origin + /api/rivet in browser, or 127.0.0.1:6420 server-side.
   */
  endpoint: z2.string().optional().transform(
    (val) => val ?? getRivetEngine() ?? getRivetEndpoint() ?? getDefaultEndpoint()
  ),
  /** Token to use to authenticate with the API. */
  token: z2.string().optional().transform((val) => val ?? getRivetToken()),
  /** Namespace to connect to. */
  namespace: z2.string().optional().transform((val) => val ?? getRivetNamespace()),
  /** Name of the runner. This is used to group together runners in to different pools. */
  runnerName: z2.string().default(() => getRivetRunner() ?? "default"),
  encoding: EncodingSchema.default("bare"),
  headers: z2.record(z2.string(), z2.string()).optional().default(() => ({})),
  // See RunConfig.getUpgradeWebSocket
  //
  // This is required in the client config in order to support
  // `proxyWebSocket`
  getUpgradeWebSocket: z2.custom().optional(),
  /** Whether to automatically perform health checks when the client is created. */
  disableMetadataLookup: z2.boolean().optional().default(false),
  /** Whether to enable RivetKit Devtools integration. */
  devtools: z2.boolean().default(
    () => {
      var _a, _b;
      return typeof window !== "undefined" && (((_a = window == null ? void 0 : window.location) == null ? void 0 : _a.hostname) === "127.0.0.1" || ((_b = window.location) == null ? void 0 : _b.hostname) === "localhost");
    }
  )
});
var ClientConfigSchema = ClientConfigSchemaBase.transform(
  (config, ctx) => transformClientConfig(config, ctx)
);
function transformClientConfig(config, ctx) {
  const parsedEndpoint = tryParseEndpoint(ctx, {
    endpoint: config.endpoint,
    path: ["endpoint"],
    namespace: config.namespace,
    token: config.token
  });
  return {
    ...config,
    endpoint: parsedEndpoint == null ? void 0 : parsedEndpoint.endpoint,
    namespace: (parsedEndpoint == null ? void 0 : parsedEndpoint.namespace) ?? config.namespace ?? "default",
    token: (parsedEndpoint == null ? void 0 : parsedEndpoint.token) ?? config.token
  };
}
function convertRegistryConfigToClientConfig(config) {
  var _a, _b;
  return {
    endpoint: config.endpoint,
    token: config.token,
    namespace: config.namespace,
    runnerName: config.runner.runnerName,
    headers: config.headers,
    encoding: "bare",
    getUpgradeWebSocket: void 0,
    // We don't need health checks for internal clients
    disableMetadataLookup: true,
    devtools: typeof window !== "undefined" && (((_a = window == null ? void 0 : window.location) == null ? void 0 : _a.hostname) === "127.0.0.1" || ((_b = window == null ? void 0 : window.location) == null ? void 0 : _b.hostname) === "localhost")
  };
}

// src/actor/instance/mod.ts
import invariant7 from "invariant";
import {
  createTraces
} from "@rivetkit/traces";

// src/inspector/actor-inspector.ts
import * as cbor6 from "cbor-x";
import { createNanoEvents } from "nanoevents";
var ActorInspector = class {
  constructor(actor) {
    this.actor = actor;
    var _a, _b;
    this.#lastQueueSize = ((_a = actor.queueManager) == null ? void 0 : _a.size) ?? 0;
    const runInspector = getRunInspectorConfig(actor.config.run);
    this.#workflowInspector = runInspector == null ? void 0 : runInspector.workflow;
    if ((_b = this.#workflowInspector) == null ? void 0 : _b.onHistoryUpdated) {
      this.#workflowInspector.onHistoryUpdated((history) => {
        this.emitter.emit(
          "workflowHistoryUpdated",
          history
        );
      });
    }
  }
  emitter = createNanoEvents();
  #lastQueueSize = 0;
  #workflowInspector;
  getQueueSize() {
    return this.#lastQueueSize;
  }
  async getQueueStatus(limit) {
    const maxSize = this.actor.config.options.maxQueueSize;
    const safeLimit = Math.max(0, Math.floor(limit));
    const messages = await this.actor.queueManager.getMessages();
    const sorted = messages.sort(
      (a, b) => a.createdAt - b.createdAt
    );
    const limited = safeLimit > 0 ? sorted.slice(0, safeLimit) : [];
    return {
      size: BigInt(this.#lastQueueSize),
      maxSize: BigInt(maxSize),
      truncated: sorted.length > limited.length,
      messages: limited.map((message) => ({
        id: message.id,
        name: message.name,
        createdAtMs: BigInt(message.createdAt)
      }))
    };
  }
  updateQueueSize(size) {
    if (this.#lastQueueSize === size) {
      return;
    }
    this.#lastQueueSize = size;
    this.emitter.emit("queueUpdated");
  }
  isWorkflowEnabled() {
    return this.#workflowInspector !== void 0;
  }
  getWorkflowHistory() {
    if (!this.#workflowInspector) {
      return null;
    }
    const history = this.#workflowInspector.getHistory();
    return history ?? null;
  }
  // actor accessor methods
  isDatabaseEnabled() {
    try {
      return this.actor.db !== void 0;
    } catch {
      return false;
    }
  }
  isStateEnabled() {
    return this.actor.stateEnabled;
  }
  getState() {
    if (!this.actor.stateEnabled) {
      throw new StateNotEnabled();
    }
    return bufferToArrayBuffer(
      cbor6.encode(this.actor.stateManager.persistRaw.state)
    );
  }
  getRpcs() {
    return this.actor.actions;
  }
  getConnections() {
    return Array.from(
      this.actor.connectionManager.connections.entries()
    ).map(([id, conn]) => {
      var _a, _b;
      const connStateManager = conn[CONN_STATE_MANAGER_SYMBOL];
      return {
        type: (_a = conn[CONN_DRIVER_SYMBOL]) == null ? void 0 : _a.type,
        id,
        details: bufferToArrayBuffer(
          cbor6.encode({
            type: (_b = conn[CONN_DRIVER_SYMBOL]) == null ? void 0 : _b.type,
            params: conn.params,
            stateEnabled: connStateManager.stateEnabled,
            state: connStateManager.stateEnabled ? connStateManager.state : void 0,
            subscriptions: conn.subscriptions.size,
            isHibernatable: conn.isHibernatable
            // TODO: Include underlying hibernatable metadata +
            // path + headers
          })
        )
      };
    });
  }
  async setState(state) {
    if (!this.actor.stateEnabled) {
      throw new StateNotEnabled();
    }
    this.actor.stateManager.state = cbor6.decode(Buffer.from(state));
    await this.actor.stateManager.saveState({ immediate: true });
  }
  async executeAction(name, params) {
    const conn = await this.actor.connectionManager.prepareAndConnectConn(
      createHttpDriver(),
      // TODO: This may cause issues
      void 0,
      void 0,
      void 0,
      void 0
    );
    try {
      return bufferToArrayBuffer(
        cbor6.encode(
          await this.actor.executeAction(
            new ActionContext(this.actor, conn),
            name,
            cbor6.decode(Buffer.from(params))
          )
        )
      );
    } finally {
      conn.disconnect();
    }
  }
};

// src/actor/conn/persisted.ts
import * as cbor7 from "cbor-x";
function convertConnToBarePersistedConn(persist) {
  return {
    id: persist.id,
    parameters: bufferToArrayBuffer(cbor7.encode(persist.parameters)),
    state: bufferToArrayBuffer(cbor7.encode(persist.state)),
    subscriptions: persist.subscriptions.map((sub) => ({
      eventName: sub.eventName
    })),
    gatewayId: persist.gatewayId,
    requestId: persist.requestId,
    serverMessageIndex: persist.serverMessageIndex,
    clientMessageIndex: persist.clientMessageIndex,
    requestPath: persist.requestPath,
    requestHeaders: new Map(Object.entries(persist.requestHeaders))
  };
}
function convertConnFromBarePersistedConn(bareData) {
  return {
    id: bareData.id,
    parameters: cbor7.decode(new Uint8Array(bareData.parameters)),
    state: cbor7.decode(new Uint8Array(bareData.state)),
    subscriptions: bareData.subscriptions.map((sub) => ({
      eventName: sub.eventName
    })),
    gatewayId: bareData.gatewayId,
    requestId: bareData.requestId,
    serverMessageIndex: bareData.serverMessageIndex,
    clientMessageIndex: bareData.clientMessageIndex,
    requestPath: bareData.requestPath,
    requestHeaders: Object.fromEntries(bareData.requestHeaders)
  };
}

// src/actor/schedule.ts
var Schedule = class {
  #actor;
  constructor(actor) {
    this.#actor = actor;
  }
  async after(duration, fn, ...args) {
    await this.#actor.scheduleEvent(Date.now() + duration, fn, args);
  }
  async at(timestamp, fn, ...args) {
    await this.#actor.scheduleEvent(timestamp, fn, args);
  }
};

// src/actor/instance/connection-manager.ts
import invariant6 from "invariant";
var ConnectionManager = class {
  #actor;
  #connections = /* @__PURE__ */ new Map();
  /** Connections that have had their state changed and need to be persisted. */
  #connsWithPersistChanged = /* @__PURE__ */ new Set();
  constructor(actor) {
    this.#actor = actor;
  }
  get connections() {
    return this.#connections;
  }
  getConnForId(id) {
    return this.#connections.get(id);
  }
  get connsWithPersistChanged() {
    return this.#connsWithPersistChanged;
  }
  clearConnWithPersistChanged() {
    this.#connsWithPersistChanged.clear();
  }
  markConnWithPersistChanged(conn) {
    invariant6(
      conn.isHibernatable,
      "cannot mark non-hibernatable conn for persist"
    );
    this.#actor.rLog.debug({
      msg: "marked connection as changed",
      connId: conn.id,
      totalChanged: this.#connsWithPersistChanged.size
    });
    this.#connsWithPersistChanged.add(conn.id);
    this.#actor.stateManager.savePersistThrottled();
  }
  // MARK: - Connection Lifecycle
  /**
   * Handles pre-connection logic (i.e. auth & create state) before actually connecting the connection.
   */
  async prepareConn(driver, params, request, requestPath, requestHeaders, isHibernatable, isRestoringHibernatable) {
    this.#actor.assertReady();
    if (isRestoringHibernatable) {
      return this.#reconnectHibernatableConn(driver);
    }
    if (this.#actor.config.onBeforeConnect) {
      const ctx = new BeforeConnectContext(this.#actor, request);
      await this.#actor.runInTraceSpan(
        "actor.onBeforeConnect",
        {
          "rivet.conn.type": driver.type
        },
        () => this.#actor.config.onBeforeConnect(ctx, params)
      );
    }
    let connState;
    if (this.#actor.connStateEnabled) {
      connState = await this.#createConnState(params, request);
    }
    let connData;
    if (isHibernatable) {
      const hibernatable = driver.hibernatable;
      invariant6(hibernatable, "must have hibernatable");
      invariant6(requestPath, "missing requestPath for hibernatable ws");
      invariant6(
        requestHeaders,
        "missing requestHeaders for hibernatable ws"
      );
      connData = {
        hibernatable: {
          id: crypto.randomUUID(),
          parameters: params,
          state: connState,
          subscriptions: [],
          gatewayId: hibernatable.gatewayId,
          requestId: hibernatable.requestId,
          clientMessageIndex: 0,
          // First message index will be 1, so we start at 0
          serverMessageIndex: 0,
          requestPath,
          requestHeaders
        }
      };
    } else {
      connData = {
        ephemeral: {
          id: crypto.randomUUID(),
          parameters: params,
          state: connState
        }
      };
    }
    const conn = new Conn(this.#actor, connData);
    conn[CONN_DRIVER_SYMBOL] = driver;
    return conn;
  }
  /**
   * Adds a connection form prepareConn to the actor and calls onConnect.
   *
   * This method is intentionally not async since it needs to be called in
   * `onOpen` for WebSockets. If this is async, the order of open events will
   * be messed up and cause race conditions that can drop WebSocket messages.
   * So all async work in prepareConn.
   */
  connectConn(conn) {
    invariant6(!this.#connections.has(conn.id), "conn already connected");
    this.#connections.set(conn.id, conn);
    if (this.#actor.driver.onCreateConn) {
      this.#actor.driver.onCreateConn(conn);
    }
    if (conn.isHibernatable) {
      this.markConnWithPersistChanged(conn);
    }
    this.#callOnConnect(conn);
    this.#actor.inspector.emitter.emit("connectionsUpdated");
    this.#actor.resetSleepTimer();
    conn[CONN_CONNECTED_SYMBOL] = true;
    if (conn[CONN_SPEAKS_RIVETKIT_SYMBOL]) {
      const initData = { actorId: this.#actor.id, connectionId: conn.id };
      conn[CONN_SEND_MESSAGE_SYMBOL](
        new CachedSerializer(
          initData,
          TO_CLIENT_VERSIONED,
          CURRENT_VERSION,
          ToClientSchema,
          // JSON: identity conversion (no nested data to encode)
          (value) => ({
            body: {
              tag: "Init",
              val: value
            }
          }),
          // BARE/CBOR: identity conversion (no nested data to encode)
          (value) => ({
            body: {
              tag: "Init",
              val: value
            }
          })
        )
      );
    }
  }
  #reconnectHibernatableConn(driver) {
    invariant6(driver.hibernatable, "missing requestIdBuf");
    const existingConn = this.findHibernatableConn(
      driver.hibernatable.gatewayId,
      driver.hibernatable.requestId
    );
    invariant6(
      existingConn,
      "cannot find connection for restoring connection"
    );
    this.#actor.rLog.debug({
      msg: "reconnecting hibernatable websocket connection",
      connectionId: existingConn.id
    });
    if (existingConn[CONN_DRIVER_SYMBOL]) {
      this.#disconnectExistingDriver(existingConn);
    }
    existingConn[CONN_DRIVER_SYMBOL] = driver;
    this.#actor.resetSleepTimer();
    existingConn[CONN_CONNECTED_SYMBOL] = true;
    this.#actor.inspector.emitter.emit("connectionsUpdated");
    return existingConn;
  }
  #disconnectExistingDriver(conn) {
    const driver = conn[CONN_DRIVER_SYMBOL];
    if (driver == null ? void 0 : driver.disconnect) {
      driver.disconnect(
        this.#actor,
        conn,
        "Reconnecting hibernatable websocket with new driver state"
      );
    }
  }
  /**
   * Handle connection disconnection.
   *
   * This is called by `Conn.disconnect`. This should not call `Conn.disconnect.`
   */
  async connDisconnected(conn) {
    var _a;
    this.#connections.delete(conn.id);
    this.#actor.rLog.debug({ msg: "removed conn", connId: conn.id });
    if (this.#actor.driver.onDestroyConn) {
      this.#actor.driver.onDestroyConn(conn);
    }
    for (const eventName of [...conn.subscriptions.values()]) {
      this.#actor.eventManager.removeSubscription(eventName, conn, true);
    }
    this.#actor.resetSleepTimer();
    this.#actor.inspector.emitter.emit("connectionsUpdated");
    const attributes = {
      "rivet.conn.id": conn.id,
      "rivet.conn.type": (_a = conn[CONN_DRIVER_SYMBOL]) == null ? void 0 : _a.type,
      "rivet.conn.hibernatable": conn.isHibernatable
    };
    const span = this.#actor.startTraceSpan(
      "actor.onDisconnect",
      attributes
    );
    try {
      if (this.#actor.config.onDisconnect) {
        const result = this.#actor.traces.withSpan(
          span,
          () => this.#actor.config.onDisconnect(
            this.#actor.actorContext,
            conn
          )
        );
        this.#actor.emitTraceEvent(
          "connection.disconnect",
          attributes,
          span
        );
        if (result instanceof Promise) {
          result.then(() => {
            this.#actor.endTraceSpan(span, { code: "OK" });
          }).catch((error) => {
            this.#actor.endTraceSpan(span, {
              code: "ERROR",
              message: stringifyError(error)
            });
            this.#actor.rLog.error({
              msg: "error in `onDisconnect`",
              error: stringifyError(error)
            });
          });
        } else {
          this.#actor.endTraceSpan(span, { code: "OK" });
        }
      } else {
        this.#actor.emitTraceEvent(
          "connection.disconnect",
          attributes,
          span
        );
        this.#actor.endTraceSpan(span, { code: "OK" });
      }
    } catch (error) {
      this.#actor.endTraceSpan(span, {
        code: "ERROR",
        message: stringifyError(error)
      });
      this.#actor.rLog.error({
        msg: "error in `onDisconnect`",
        error: stringifyError(error)
      });
    }
    this.#connsWithPersistChanged.delete(conn.id);
    if (conn.isHibernatable) {
      const key = makeConnKey(conn.id);
      try {
        await this.#actor.driver.kvBatchDelete(this.#actor.id, [key]);
        this.#actor.rLog.debug({
          msg: "removed connection from KV",
          connId: conn.id
        });
      } catch (err) {
        this.#actor.rLog.error({
          msg: "kvBatchDelete failed for conn",
          err: stringifyError(err)
        });
      }
    }
  }
  /**
   * Utilify function for call sites that don't need a separate prepare and connect phase.
   */
  async prepareAndConnectConn(driver, params, request, requestPath, requestHeaders) {
    const conn = await this.prepareConn(
      driver,
      params,
      request,
      requestPath,
      requestHeaders,
      false,
      false
    );
    this.connectConn(conn);
    return conn;
  }
  // MARK: - Persistence
  /**
   * Restores connections from persisted data during actor initialization.
   */
  restoreConnections(connections) {
    for (const connPersist of connections) {
      const conn = new Conn(this.#actor, {
        hibernatable: connPersist
      });
      this.#connections.set(conn.id, conn);
      if (this.#actor.driver.onCreateConn) {
        this.#actor.driver.onCreateConn(conn);
      }
      for (const sub of connPersist.subscriptions) {
        this.#actor.eventManager.addSubscription(
          sub.eventName,
          conn,
          true
        );
      }
    }
  }
  // MARK: - Private Helpers
  findHibernatableConn(gatewayIdBuf, requestIdBuf) {
    return Array.from(this.#connections.values()).find((conn) => {
      const connStateManager = conn[CONN_STATE_MANAGER_SYMBOL];
      const h = connStateManager.hibernatableDataRaw;
      return h && arrayBuffersEqual(h.gatewayId, gatewayIdBuf) && arrayBuffersEqual(h.requestId, requestIdBuf);
    });
  }
  async #createConnState(params, request) {
    if ("createConnState" in this.#actor.config) {
      const createConnState = this.#actor.config.createConnState;
      const ctx = new CreateConnStateContext(this.#actor, request);
      return await this.#actor.runInTraceSpan(
        "actor.createConnState",
        void 0,
        () => {
          const dataOrPromise = createConnState(ctx, params);
          if (dataOrPromise instanceof Promise) {
            return deadline(
              dataOrPromise,
              this.#actor.config.options.createConnStateTimeout
            );
          }
          return dataOrPromise;
        }
      );
    } else if ("connState" in this.#actor.config) {
      return structuredClone(this.#actor.config.connState);
    }
    throw new Error(
      "Could not create connection state from 'createConnState' or 'connState'"
    );
  }
  #callOnConnect(conn) {
    var _a;
    const attributes = {
      "rivet.conn.id": conn.id,
      "rivet.conn.type": (_a = conn[CONN_DRIVER_SYMBOL]) == null ? void 0 : _a.type,
      "rivet.conn.hibernatable": conn.isHibernatable
    };
    const span = this.#actor.startTraceSpan("actor.onConnect", attributes);
    try {
      if (this.#actor.config.onConnect) {
        const ctx = new ConnectContext(this.#actor, conn);
        const result = this.#actor.traces.withSpan(
          span,
          () => this.#actor.config.onConnect(ctx, conn)
        );
        this.#actor.emitTraceEvent(
          "connection.connect",
          attributes,
          span
        );
        if (result instanceof Promise) {
          deadline(
            result,
            this.#actor.config.options.onConnectTimeout
          ).then(() => {
            this.#actor.endTraceSpan(span, { code: "OK" });
          }).catch((error) => {
            this.#actor.endTraceSpan(span, {
              code: "ERROR",
              message: stringifyError(error)
            });
            this.#actor.rLog.error({
              msg: "error in `onConnect`, closing socket",
              error
            });
            conn == null ? void 0 : conn.disconnect("`onConnect` failed");
          });
          return;
        }
      }
      this.#actor.emitTraceEvent("connection.connect", attributes, span);
      this.#actor.endTraceSpan(span, { code: "OK" });
    } catch (error) {
      this.#actor.endTraceSpan(span, {
        code: "ERROR",
        message: stringifyError(error)
      });
      this.#actor.rLog.error({
        msg: "error in `onConnect`",
        error: stringifyError(error)
      });
      conn == null ? void 0 : conn.disconnect("`onConnect` failed");
    }
  }
};

// src/actor/instance/event-manager.ts
import * as cbor8 from "cbor-x";
var EventManager = class {
  #actor;
  #subscriptionIndex = /* @__PURE__ */ new Map();
  constructor(actor) {
    this.#actor = actor;
  }
  // MARK: - Public API
  /**
   * Adds a subscription for a connection to an event.
   *
   * @param eventName - The name of the event to subscribe to
   * @param connection - The connection subscribing to the event
   * @param fromPersist - Whether this subscription is being restored from persistence
   */
  addSubscription(eventName, connection, fromPersist) {
    if (connection.subscriptions.has(eventName)) {
      this.#actor.rLog.debug({
        msg: "connection already has subscription",
        eventName,
        connId: connection.id
      });
      return;
    }
    connection.subscriptions.add(eventName);
    let subscribers = this.#subscriptionIndex.get(eventName);
    if (!subscribers) {
      subscribers = /* @__PURE__ */ new Set();
      this.#subscriptionIndex.set(eventName, subscribers);
    }
    subscribers.add(connection);
    if (!fromPersist) {
      connection[CONN_STATE_MANAGER_SYMBOL].addSubscription({
        eventName
      });
      this.#actor.stateManager.saveState({ immediate: true });
    }
    this.#actor.rLog.debug({
      msg: "subscription added",
      eventName,
      connId: connection.id,
      totalSubscribers: subscribers.size
    });
  }
  /**
   * Removes a subscription for a connection from an event.
   *
   * @param eventName - The name of the event to unsubscribe from
   * @param connection - The connection unsubscribing from the event
   * @param fromRemoveConn - Whether this is being called as part of connection removal
   */
  removeSubscription(eventName, connection, fromRemoveConn) {
    if (!connection.subscriptions.has(eventName)) {
      this.#actor.rLog.warn({
        msg: "connection does not have subscription",
        eventName,
        connId: connection.id
      });
      return;
    }
    connection.subscriptions.delete(eventName);
    const subscribers = this.#subscriptionIndex.get(eventName);
    if (subscribers) {
      subscribers.delete(connection);
      if (subscribers.size === 0) {
        this.#subscriptionIndex.delete(eventName);
      }
    }
    if (!fromRemoveConn) {
      const removed = connection[CONN_STATE_MANAGER_SYMBOL].removeSubscription({ eventName });
      if (!removed) {
        this.#actor.rLog.warn({
          msg: "subscription does not exist in persist",
          eventName,
          connId: connection.id
        });
      }
      this.#actor.stateManager.saveState({ immediate: true });
    }
    this.#actor.rLog.debug({
      msg: "subscription removed",
      eventName,
      connId: connection.id,
      remainingSubscribers: (subscribers == null ? void 0 : subscribers.size) || 0
    });
  }
  /**
   * Broadcasts an event to all subscribed connections.
   *
   * @param name - The name of the event to broadcast
   * @param args - The arguments to send with the event
   */
  broadcast(name, ...args) {
    this.#actor.assertReady();
    const subscribers = this.#subscriptionIndex.get(name);
    if (!subscribers || subscribers.size === 0) {
      this.#actor.rLog.debug({
        msg: "no subscribers for event",
        eventName: name
      });
      return;
    }
    this.#actor.emitTraceEvent("message.broadcast", {
      "rivet.event.name": name,
      "rivet.broadcast.subscribers": subscribers.size
    });
    const eventData = { name, args };
    const toClientSerializer = new CachedSerializer(
      eventData,
      TO_CLIENT_VERSIONED,
      CURRENT_VERSION,
      ToClientSchema,
      // JSON: args is the raw value (array of arguments)
      (value) => ({
        body: {
          tag: "Event",
          val: {
            name: value.name,
            args: value.args
          }
        }
      }),
      // BARE/CBOR: args needs to be CBOR-encoded to ArrayBuffer
      (value) => ({
        body: {
          tag: "Event",
          val: {
            name: value.name,
            args: bufferToArrayBuffer(cbor8.encode(value.args))
          }
        }
      })
    );
    let sentCount = 0;
    for (const connection of subscribers) {
      if (connection[CONN_SPEAKS_RIVETKIT_SYMBOL]) {
        try {
          connection[CONN_SEND_MESSAGE_SYMBOL](toClientSerializer);
          sentCount++;
        } catch (error) {
          if (error instanceof OutgoingMessageTooLong) {
            throw error;
          }
          this.#actor.rLog.error({
            msg: "failed to send event to connection",
            eventName: name,
            connId: connection.id,
            error: error instanceof Error ? error.message : String(error)
          });
        }
      }
    }
    this.#actor.rLog.debug({
      msg: "event broadcasted",
      eventName: name,
      subscriberCount: subscribers.size,
      sentCount
    });
  }
  /**
   * Gets all subscribers for a specific event.
   *
   * @param eventName - The name of the event
   * @returns Set of connections subscribed to the event, or undefined if no subscribers
   */
  getSubscribers(eventName) {
    return this.#subscriptionIndex.get(eventName);
  }
  /**
   * Gets all events and their subscriber counts.
   *
   * @returns Map of event names to subscriber counts
   */
  getEventStats() {
    const stats = /* @__PURE__ */ new Map();
    for (const [eventName, subscribers] of this.#subscriptionIndex) {
      stats.set(eventName, subscribers.size);
    }
    return stats;
  }
  /**
   * Clears all subscriptions for a connection.
   * Used during connection cleanup.
   *
   * @param connection - The connection to clear subscriptions for
   */
  clearConnectionSubscriptions(connection) {
    for (const eventName of [...connection.subscriptions.values()]) {
      this.removeSubscription(eventName, connection, true);
    }
  }
  /**
   * Gets the total number of unique events being subscribed to.
   */
  get eventCount() {
    return this.#subscriptionIndex.size;
  }
  /**
   * Gets the total number of subscriptions across all events.
   */
  get totalSubscriptionCount() {
    let total = 0;
    for (const subscribers of this.#subscriptionIndex.values()) {
      total += subscribers.size;
    }
    return total;
  }
  /**
   * Checks if an event has any subscribers.
   *
   * @param eventName - The name of the event to check
   * @returns True if the event has at least one subscriber
   */
  hasSubscribers(eventName) {
    const subscribers = this.#subscriptionIndex.get(eventName);
    return subscribers !== void 0 && subscribers.size > 0;
  }
};

// src/actor/instance/traces-driver.ts
function concatPrefix(prefix, key) {
  const merged = new Uint8Array(prefix.length + key.length);
  merged.set(prefix, 0);
  merged.set(key, prefix.length);
  return merged;
}
function stripPrefix(prefix, key) {
  return key.slice(prefix.length);
}
function compareBytes(a, b) {
  const len = Math.min(a.length, b.length);
  for (let i = 0; i < len; i++) {
    if (a[i] !== b[i]) {
      return a[i] - b[i];
    }
  }
  return a.length - b.length;
}
var ActorTracesDriver = class {
  #driver;
  #actorId;
  #prefix;
  constructor(driver, actorId) {
    this.#driver = driver;
    this.#actorId = actorId;
    this.#prefix = KEYS.TRACES_PREFIX;
  }
  async get(key) {
    const [value] = await this.#driver.kvBatchGet(this.#actorId, [
      concatPrefix(this.#prefix, key)
    ]);
    return value ?? null;
  }
  async set(key, value) {
    await this.#driver.kvBatchPut(this.#actorId, [
      [concatPrefix(this.#prefix, key), value]
    ]);
  }
  async delete(key) {
    await this.#driver.kvBatchDelete(this.#actorId, [
      concatPrefix(this.#prefix, key)
    ]);
  }
  async deletePrefix(prefix) {
    const fullPrefix = concatPrefix(this.#prefix, prefix);
    const entries = await this.#driver.kvListPrefix(
      this.#actorId,
      fullPrefix
    );
    if (entries.length === 0) {
      return;
    }
    await this.#driver.kvBatchDelete(
      this.#actorId,
      entries.map(([key]) => key)
    );
  }
  async list(prefix) {
    const fullPrefix = concatPrefix(this.#prefix, prefix);
    const entries = await this.#driver.kvListPrefix(
      this.#actorId,
      fullPrefix
    );
    return entries.map(([key, value]) => ({
      key: stripPrefix(this.#prefix, key),
      value
    }));
  }
  async listRange(start, end, options) {
    const fullStart = concatPrefix(this.#prefix, start);
    const fullEnd = concatPrefix(this.#prefix, end);
    const entries = await this.#driver.kvListPrefix(
      this.#actorId,
      this.#prefix
    );
    const filtered = entries.filter(([key]) => {
      return compareBytes(key, fullStart) >= 0 && compareBytes(key, fullEnd) < 0;
    }).sort(([keyA], [keyB]) => compareBytes(keyA, keyB));
    if (options == null ? void 0 : options.reverse) {
      filtered.reverse();
    }
    const limited = (options == null ? void 0 : options.limit) ? filtered.slice(0, options.limit) : filtered;
    return limited.map(([key, value]) => ({
      key: stripPrefix(this.#prefix, key),
      value
    }));
  }
  async batch(writes) {
    if (writes.length === 0) {
      return;
    }
    await this.#driver.kvBatchPut(
      this.#actorId,
      writes.map(({ key, value }) => [
        concatPrefix(this.#prefix, key),
        value
      ])
    );
  }
};

// src/actor/instance/persisted.ts
import * as cbor9 from "cbor-x";
function convertActorToBarePersisted(persist) {
  return {
    input: persist.input !== void 0 ? bufferToArrayBuffer(cbor9.encode(persist.input)) : null,
    hasInitialized: persist.hasInitialized,
    state: bufferToArrayBuffer(cbor9.encode(persist.state)),
    scheduledEvents: persist.scheduledEvents.map((event) => ({
      eventId: event.eventId,
      timestamp: BigInt(event.timestamp),
      action: event.action,
      args: event.args ?? null
    }))
  };
}
function convertActorFromBarePersisted(bareData) {
  return {
    input: bareData.input ? cbor9.decode(new Uint8Array(bareData.input)) : void 0,
    hasInitialized: bareData.hasInitialized,
    state: cbor9.decode(new Uint8Array(bareData.state)),
    scheduledEvents: bareData.scheduledEvents.map((event) => ({
      eventId: event.eventId,
      timestamp: Number(event.timestamp),
      action: event.action,
      args: event.args ?? void 0
    }))
  };
}

// src/actor/instance/queue-manager.ts
import * as cbor10 from "cbor-x";
var DEFAULT_METADATA = {
  nextId: 1n,
  size: 0
};
var PENDING_WARNING_MS = 3e4;
var BACKOFF_INITIAL_MS = 1e3;
var BACKOFF_MAX_MS = 5 * 6e4;
var QueueManager = class {
  #actor;
  #driver;
  #waiters = /* @__PURE__ */ new Map();
  #completionWaiters = /* @__PURE__ */ new Map();
  #metadata = { ...DEFAULT_METADATA };
  #pendingMessageId;
  #pendingWarningHandle;
  #redeliveryTimeout;
  #redeliveryAt;
  #messageListeners = /* @__PURE__ */ new Set();
  constructor(actor, driver) {
    this.#actor = actor;
    this.#driver = driver;
  }
  /** Returns the current number of messages in the queue. */
  get size() {
    return this.#metadata.size;
  }
  /** Loads queue metadata from storage and initializes internal state. */
  async initialize() {
    const [metadataBuffer] = await this.#driver.kvBatchGet(this.#actor.id, [
      KEYS.QUEUE_METADATA
    ]);
    if (!metadataBuffer) {
      await this.#driver.kvBatchPut(this.#actor.id, [
        [KEYS.QUEUE_METADATA, this.#serializeMetadata()]
      ]);
      this.#actor.inspector.updateQueueSize(this.#metadata.size);
      return;
    }
    try {
      const decoded = QUEUE_METADATA_VERSIONED.deserializeWithEmbeddedVersion(
        metadataBuffer
      );
      this.#metadata.nextId = decoded.nextId;
      this.#metadata.size = Number(decoded.size);
    } catch (error) {
      this.#actor.rLog.error({
        msg: "failed to decode queue metadata, rebuilding from messages",
        error
      });
      await this.#rebuildMetadata();
    }
    this.#actor.inspector.updateQueueSize(this.#metadata.size);
    await this.#recoverInFlightMessages();
  }
  /** Adds a message to the queue with the given name and body. */
  async enqueue(name, body, options = {}) {
    this.#actor.assertReady();
    const sizeLimit = this.#actor.config.options.maxQueueSize;
    if (this.#metadata.size >= sizeLimit) {
      throw new QueueFull(sizeLimit);
    }
    let invalidPath = "";
    if (!isCborSerializable(body, (path) => {
      invalidPath = path;
    })) {
      throw new QueueMessageInvalid(invalidPath);
    }
    const createdAt = Date.now();
    const bodyCborBuffer = cbor10.encode(body);
    const availableAt = createdAt;
    const encodedMessage = QUEUE_MESSAGE_VERSIONED.serializeWithEmbeddedVersion(
      {
        name,
        body: new Uint8Array(bodyCborBuffer).buffer,
        createdAt: BigInt(createdAt),
        failureCount: 0,
        availableAt: BigInt(availableAt),
        inFlight: false,
        inFlightAt: null
      },
      CURRENT_VERSION2
    );
    const encodedSize = encodedMessage.byteLength;
    if (encodedSize > this.#actor.config.options.maxQueueMessageSize) {
      throw new QueueMessageTooLarge(
        encodedSize,
        this.#actor.config.options.maxQueueMessageSize
      );
    }
    const id = this.#metadata.nextId;
    const messageKey = makeQueueMessageKey(id);
    this.#metadata.nextId = id + 1n;
    this.#metadata.size += 1;
    const encodedMetadata = this.#serializeMetadata();
    await this.#driver.kvBatchPut(this.#actor.id, [
      [messageKey, encodedMessage],
      [KEYS.QUEUE_METADATA, encodedMetadata]
    ]);
    this.#actor.inspector.updateQueueSize(this.#metadata.size);
    const message = {
      id,
      name,
      body,
      createdAt,
      failureCount: 0,
      availableAt,
      inFlight: false,
      inFlightAt: void 0
    };
    this.#actor.resetSleepTimer();
    if (!options.deferWaiters) {
      await this.#maybeResolveWaiters();
    }
    this.#notifyMessageListeners(name);
    return message;
  }
  async enqueueAndWait(name, body, timeout) {
    const message = await this.enqueue(name, body, {
      deferWaiters: true
    });
    const completionPromise = this.waitForCompletion(message.id, timeout);
    await this.#maybeResolveWaiters();
    return await completionPromise;
  }
  /** Receives messages from the queue matching the given names. Waits until messages are available or timeout is reached. */
  async receive(names, count, timeout, abortSignal, wait = false) {
    this.#actor.assertReady();
    if (this.#pendingMessageId !== void 0) {
      throw new QueueMessagePending();
    }
    const limitedCount = Math.max(1, count);
    const nameSet = new Set(names);
    const immediate = await this.#drainMessages(
      nameSet,
      limitedCount,
      wait
    );
    if (immediate.length > 0 || timeout === 0) {
      return timeout === 0 && immediate.length === 0 ? [] : immediate;
    }
    const { promise, resolve, reject } = promiseWithResolvers();
    const waiterId = crypto.randomUUID();
    const waiter = {
      id: waiterId,
      nameSet,
      count: limitedCount,
      wait,
      resolve,
      reject,
      signal: abortSignal
    };
    if (timeout !== void 0) {
      waiter.timeoutHandle = setTimeout(() => {
        this.#waiters.delete(waiterId);
        resolve([]);
      }, timeout);
    }
    const onAbort = () => {
      this.#waiters.delete(waiterId);
      if (waiter.timeoutHandle) {
        clearTimeout(waiter.timeoutHandle);
      }
      reject(new ActorAborted());
    };
    const onStop = () => {
      this.#waiters.delete(waiterId);
      if (waiter.timeoutHandle) {
        clearTimeout(waiter.timeoutHandle);
      }
      reject(new ActorAborted());
    };
    const actorAbortSignal = this.#actor.abortSignal;
    if (actorAbortSignal.aborted) {
      onStop();
      return promise;
    }
    actorAbortSignal.addEventListener("abort", onStop, { once: true });
    if (abortSignal) {
      if (abortSignal.aborted) {
        onAbort();
        return promise;
      }
      abortSignal.addEventListener("abort", onAbort, { once: true });
    }
    this.#waiters.set(waiterId, waiter);
    return promise;
  }
  /** Waits for a specific queue message to complete. */
  async waitForCompletion(messageId, timeout) {
    const { promise, resolve, reject } = promiseWithResolvers();
    const waiterId = crypto.randomUUID();
    const waiter = {
      id: waiterId,
      messageId,
      resolve,
      reject
    };
    if (timeout !== void 0) {
      waiter.timeoutHandle = setTimeout(() => {
        this.#completionWaiters.delete(messageId);
        resolve({ status: "timedOut" });
      }, timeout);
    }
    this.#completionWaiters.set(messageId, waiter);
    const [messageEntry] = await this.#driver.kvBatchGet(this.#actor.id, [
      makeQueueMessageKey(messageId)
    ]);
    if (!messageEntry) {
      const existing = this.#completionWaiters.get(messageId);
      if ((existing == null ? void 0 : existing.id) === waiterId) {
        this.#completionWaiters.delete(messageId);
        if (existing.timeoutHandle) {
          clearTimeout(existing.timeoutHandle);
        }
        resolve({ status: "completed", response: void 0 });
      }
    }
    return promise;
  }
  /** Completes a pending message and optionally responds to any waiter. */
  async complete(message, response) {
    if (this.#pendingMessageId !== message.id) {
      throw new QueueAlreadyCompleted();
    }
    this.#pendingMessageId = void 0;
    if (this.#pendingWarningHandle) {
      clearTimeout(this.#pendingWarningHandle);
      this.#pendingWarningHandle = void 0;
    }
    await this.#removeMessages([message], { resolveWaiters: false });
    this.#resolveCompletionWaiter(message.id, {
      status: "completed",
      response
    });
    await this.#maybeResolveWaiters();
  }
  /** Waits for messages with any of the specified names to appear in the queue. */
  async waitForNames(names, abortSignal) {
    const nameSet = new Set(names);
    const existing = await this.#loadQueueMessages();
    if (existing.some((message) => nameSet.has(message.name))) {
      return;
    }
    return await new Promise((resolve, reject) => {
      const listener = {
        nameSet,
        resolve: () => {
          this.#removeMessageListener(listener);
          resolve();
        },
        reject: (error) => {
          this.#removeMessageListener(listener);
          reject(error);
        }
      };
      const actorAbortSignal = this.#actor.abortSignal;
      const onActorAbort = () => listener.reject(new ActorAborted());
      if (actorAbortSignal.aborted) {
        onActorAbort();
        return;
      }
      actorAbortSignal.addEventListener("abort", onActorAbort, {
        once: true
      });
      listener.actorAbortCleanup = () => actorAbortSignal.removeEventListener("abort", onActorAbort);
      if (abortSignal) {
        const onAbort = () => listener.reject(new ActorAborted());
        if (abortSignal.aborted) {
          onAbort();
          return;
        }
        abortSignal.addEventListener("abort", onAbort, { once: true });
        listener.signalAbortCleanup = () => abortSignal.removeEventListener("abort", onAbort);
      }
      this.#messageListeners.add(listener);
    });
  }
  /** Returns all messages currently in the queue without removing them. */
  async getMessages() {
    return await this.#loadQueueMessages();
  }
  /** Deletes messages matching the provided IDs. Returns the IDs that were removed. */
  async deleteMessagesById(ids) {
    if (ids.length === 0) {
      return [];
    }
    const idSet = new Set(ids.map((id) => id.toString()));
    const entries = await this.#loadQueueMessages();
    const toRemove = entries.filter(
      (entry) => idSet.has(entry.id.toString())
    );
    if (toRemove.length === 0) {
      return [];
    }
    await this.#removeMessages(toRemove, { resolveWaiters: true });
    return toRemove.map((entry) => entry.id);
  }
  async #drainMessages(nameSet, count, wait) {
    if (this.#metadata.size === 0) {
      return [];
    }
    const now = Date.now();
    const entries = await this.#loadQueueMessages();
    const matched = entries.filter(
      (entry) => nameSet.has(entry.name) && !entry.inFlight
    );
    if (matched.length === 0) {
      return [];
    }
    const eligible = matched.filter((entry) => entry.availableAt <= now);
    if (eligible.length === 0) {
      this.#scheduleRedelivery(matched);
      return [];
    }
    const selected = eligible.slice(0, wait ? 1 : count);
    if (wait) {
      await this.#markMessageInFlight(selected[0], now);
      return [selected[0]];
    }
    await this.#removeMessages(selected, { resolveWaiters: true });
    for (const message of selected) {
      this.#actor.emitTraceEvent("queue.message.receive", {
        "rivet.queue.name": message.name,
        "rivet.queue.message_id": message.id.toString(),
        "rivet.queue.created_at_ms": message.createdAt,
        "rivet.queue.latency_ms": now - message.createdAt
      });
    }
    return selected;
  }
  async #loadQueueMessages() {
    const entries = await this.#driver.kvListPrefix(
      this.#actor.id,
      KEYS.QUEUE_PREFIX
    );
    const decoded = [];
    for (const [key, value] of entries) {
      try {
        const messageId = decodeQueueMessageKey(key);
        const decodedPayload = QUEUE_MESSAGE_VERSIONED.deserializeWithEmbeddedVersion(
          value
        );
        const body = cbor10.decode(new Uint8Array(decodedPayload.body));
        const failureCount = decodedPayload.failureCount !== void 0 && decodedPayload.failureCount !== null ? Number(decodedPayload.failureCount) : 0;
        const availableAt = decodedPayload.availableAt !== void 0 && decodedPayload.availableAt !== null ? Number(decodedPayload.availableAt) : Number(decodedPayload.createdAt);
        const inFlight = decodedPayload.inFlight ?? false;
        const inFlightAt = decodedPayload.inFlightAt !== void 0 && decodedPayload.inFlightAt !== null ? Number(decodedPayload.inFlightAt) : void 0;
        decoded.push({
          id: messageId,
          name: decodedPayload.name,
          body,
          createdAt: Number(decodedPayload.createdAt),
          failureCount,
          availableAt,
          inFlight,
          inFlightAt
        });
      } catch (error) {
        this.#actor.rLog.error({
          msg: "failed to decode queue message",
          error
        });
      }
    }
    decoded.sort((a, b) => a.id < b.id ? -1 : a.id > b.id ? 1 : 0);
    if (this.#metadata.size !== decoded.length) {
      this.#metadata.size = decoded.length;
      this.#actor.inspector.updateQueueSize(this.#metadata.size);
    }
    return decoded;
  }
  #removeMessageListener(listener) {
    var _a, _b;
    if (this.#messageListeners.delete(listener)) {
      (_a = listener.actorAbortCleanup) == null ? void 0 : _a.call(listener);
      (_b = listener.signalAbortCleanup) == null ? void 0 : _b.call(listener);
    }
  }
  #notifyMessageListeners(name) {
    if (this.#messageListeners.size === 0) {
      return;
    }
    for (const listener of [...this.#messageListeners]) {
      if (!listener.nameSet.has(name)) {
        continue;
      }
      this.#removeMessageListener(listener);
      listener.resolve();
    }
  }
  async #removeMessages(messages, options) {
    if (messages.length === 0) {
      return;
    }
    const keys = messages.map((message) => makeQueueMessageKey(message.id));
    this.#metadata.size = Math.max(0, this.#metadata.size - messages.length);
    await this.#driver.kvBatchDelete(this.#actor.id, keys);
    await this.#driver.kvBatchPut(this.#actor.id, [
      [KEYS.QUEUE_METADATA, this.#serializeMetadata()]
    ]);
    this.#actor.inspector.updateQueueSize(this.#metadata.size);
    if (options.resolveWaiters) {
      for (const message of messages) {
        this.#resolveCompletionWaiter(message.id, {
          status: "completed",
          response: void 0
        });
      }
    }
  }
  async #maybeResolveWaiters() {
    var _a;
    if (this.#pendingMessageId !== void 0) {
      return;
    }
    if (this.#redeliveryTimeout) {
      clearTimeout(this.#redeliveryTimeout);
      this.#redeliveryTimeout = void 0;
      this.#redeliveryAt = void 0;
    }
    if (this.#waiters.size === 0) {
      return;
    }
    const pending = [...this.#waiters.values()];
    for (const waiter of pending) {
      if ((_a = waiter.signal) == null ? void 0 : _a.aborted) {
        this.#waiters.delete(waiter.id);
        waiter.reject(new ActorAborted());
        continue;
      }
      const messages = await this.#drainMessages(
        waiter.nameSet,
        waiter.count,
        waiter.wait
      );
      if (messages.length === 0) {
        continue;
      }
      this.#waiters.delete(waiter.id);
      if (waiter.timeoutHandle) {
        clearTimeout(waiter.timeoutHandle);
      }
      waiter.resolve(messages);
      if (waiter.wait) {
        break;
      }
    }
  }
  /** Rebuilds metadata by scanning existing queue messages. Used when metadata is corrupted. */
  async #rebuildMetadata() {
    const entries = await this.#driver.kvListPrefix(
      this.#actor.id,
      KEYS.QUEUE_PREFIX
    );
    let maxId = 0n;
    for (const [key] of entries) {
      try {
        const messageId = decodeQueueMessageKey(key);
        if (messageId > maxId) {
          maxId = messageId;
        }
      } catch {
      }
    }
    this.#metadata.nextId = maxId + 1n;
    this.#metadata.size = entries.length;
    await this.#driver.kvBatchPut(this.#actor.id, [
      [KEYS.QUEUE_METADATA, this.#serializeMetadata()]
    ]);
    this.#actor.inspector.updateQueueSize(this.#metadata.size);
  }
  async #markMessageInFlight(message, now) {
    if (message.inFlight) {
      throw new QueueMessagePending();
    }
    message.inFlight = true;
    message.inFlightAt = now;
    await this.#persistMessage(message);
    this.#pendingMessageId = message.id;
    this.#pendingWarningHandle = setTimeout(() => {
      if (this.#pendingMessageId === message.id) {
        this.#actor.rLog.warn({
          msg: "queue message pending for over 30s",
          messageId: message.id.toString(),
          name: message.name
        });
      }
    }, PENDING_WARNING_MS);
  }
  async #persistMessage(message) {
    const bodyCborBuffer = cbor10.encode(message.body);
    const encodedMessage = QUEUE_MESSAGE_VERSIONED.serializeWithEmbeddedVersion(
      {
        name: message.name,
        body: new Uint8Array(bodyCborBuffer).buffer,
        createdAt: BigInt(message.createdAt),
        failureCount: message.failureCount,
        availableAt: BigInt(message.availableAt),
        inFlight: message.inFlight,
        inFlightAt: message.inFlightAt !== void 0 ? BigInt(message.inFlightAt) : null
      },
      CURRENT_VERSION2
    );
    await this.#driver.kvBatchPut(this.#actor.id, [
      [makeQueueMessageKey(message.id), encodedMessage]
    ]);
  }
  async #recoverInFlightMessages() {
    const entries = await this.#driver.kvListPrefix(
      this.#actor.id,
      KEYS.QUEUE_PREFIX
    );
    const updates = [];
    const now = Date.now();
    for (const [key, value] of entries) {
      try {
        const messageId = decodeQueueMessageKey(key);
        const decodedPayload = QUEUE_MESSAGE_VERSIONED.deserializeWithEmbeddedVersion(
          value
        );
        const inFlight = decodedPayload.inFlight ?? false;
        if (!inFlight) {
          continue;
        }
        const failureCount = (decodedPayload.failureCount !== void 0 && decodedPayload.failureCount !== null ? Number(decodedPayload.failureCount) : 0) + 1;
        const availableAt = now + this.#computeBackoffMs(failureCount);
        const updatedMessage = QUEUE_MESSAGE_VERSIONED.serializeWithEmbeddedVersion(
          {
            name: decodedPayload.name,
            body: decodedPayload.body,
            createdAt: decodedPayload.createdAt,
            failureCount,
            availableAt: BigInt(availableAt),
            inFlight: false,
            inFlightAt: null
          },
          CURRENT_VERSION2
        );
        updates.push([key, updatedMessage]);
        this.#actor.rLog.warn({
          msg: "recovering in-flight queue message",
          messageId: messageId.toString(),
          failureCount,
          availableAt
        });
      } catch (error) {
        this.#actor.rLog.error({
          msg: "failed to recover in-flight queue message",
          error
        });
      }
    }
    if (updates.length > 0) {
      await this.#driver.kvBatchPut(this.#actor.id, updates);
    }
  }
  #scheduleRedelivery(messages) {
    if (messages.length === 0) {
      return;
    }
    const nextAvailableAt = messages.reduce((min, message) => {
      return message.availableAt < min ? message.availableAt : min;
    }, messages[0].availableAt);
    if (this.#redeliveryAt !== void 0 && this.#redeliveryAt <= nextAvailableAt) {
      return;
    }
    if (this.#redeliveryTimeout) {
      clearTimeout(this.#redeliveryTimeout);
    }
    const delay = Math.max(0, nextAvailableAt - Date.now());
    this.#redeliveryAt = nextAvailableAt;
    this.#redeliveryTimeout = setTimeout(() => {
      this.#redeliveryTimeout = void 0;
      this.#redeliveryAt = void 0;
      void this.#maybeResolveWaiters();
    }, delay);
  }
  #resolveCompletionWaiter(messageId, result) {
    const waiter = this.#completionWaiters.get(messageId);
    if (!waiter) {
      return;
    }
    this.#completionWaiters.delete(messageId);
    if (waiter.timeoutHandle) {
      clearTimeout(waiter.timeoutHandle);
    }
    waiter.resolve(result);
  }
  #computeBackoffMs(failureCount) {
    const exp = Math.max(0, failureCount - 1);
    const delay = Math.min(
      BACKOFF_MAX_MS,
      BACKOFF_INITIAL_MS * 2 ** exp
    );
    return delay;
  }
  #serializeMetadata() {
    return QUEUE_METADATA_VERSIONED.serializeWithEmbeddedVersion(
      {
        nextId: this.#metadata.nextId,
        size: this.#metadata.size
      },
      CURRENT_VERSION2
    );
  }
};

// src/actor/instance/schedule-manager.ts
import * as cbor11 from "cbor-x";
var ScheduleManager = class {
  #actor;
  #actorDriver;
  #alarmWriteQueue = new SinglePromiseQueue();
  #config;
  // ActorConfig type
  #persist;
  // Reference to PersistedActor
  constructor(actor, actorDriver, config) {
    this.#actor = actor;
    this.#actorDriver = actorDriver;
    this.#config = config;
  }
  // MARK: - Public API
  /**
   * Sets the persist object reference.
   * Called after StateManager initializes the persist proxy.
   */
  setPersist(persist) {
    this.#persist = persist;
  }
  /**
   * Schedules an event to be executed at a specific timestamp.
   *
   * @param timestamp - Unix timestamp in milliseconds when the event should fire
   * @param action - The name of the action to execute
   * @param args - Arguments to pass to the action
   */
  async scheduleEvent(timestamp, action, args) {
    const newEvent = {
      eventId: crypto.randomUUID(),
      timestamp,
      action,
      args: bufferToArrayBuffer(cbor11.encode(args))
    };
    this.#actor.emitTraceEvent("schedule.created", {
      "rivet.schedule.event_id": newEvent.eventId,
      "rivet.schedule.action": newEvent.action,
      "rivet.schedule.timestamp_ms": newEvent.timestamp
    });
    await this.#scheduleEventInner(newEvent);
  }
  /**
   * Triggers any pending alarms that are due.
   * This method is idempotent and safe to call multiple times.
   */
  async onAlarm() {
    var _a, _b, _c;
    const now = Date.now();
    this.#actor.log.debug({
      msg: "alarm triggered",
      now,
      events: ((_b = (_a = this.#persist) == null ? void 0 : _a.scheduledEvents) == null ? void 0 : _b.length) || 0
    });
    if (!((_c = this.#persist) == null ? void 0 : _c.scheduledEvents)) {
      this.#actor.rLog.debug({ msg: "no scheduled events" });
      return;
    }
    const dueIndex = this.#persist.scheduledEvents.findIndex(
      (x) => x.timestamp <= now
    );
    if (dueIndex === -1) {
      this.#actor.rLog.debug({ msg: "no events are due yet" });
      if (this.#persist.scheduledEvents.length > 0) {
        const nextTs = this.#persist.scheduledEvents[0].timestamp;
        this.#actor.log.debug({
          msg: "alarm fired early, rescheduling for next event",
          now,
          nextTs,
          delta: nextTs - now
        });
        await this.#queueSetAlarm(nextTs);
      }
      return;
    }
    const dueEvents = this.#persist.scheduledEvents.splice(0, dueIndex + 1);
    this.#actor.log.debug({
      msg: "running events",
      count: dueEvents.length
    });
    if (this.#persist.scheduledEvents.length > 0) {
      const nextTs = this.#persist.scheduledEvents[0].timestamp;
      this.#actor.log.info({
        msg: "setting next alarm",
        nextTs,
        remainingEvents: this.#persist.scheduledEvents.length
      });
      await this.#queueSetAlarm(nextTs);
    }
    await this.#executeDueEvents(dueEvents);
  }
  /**
   * Initializes alarms on actor startup.
   * Sets the alarm for the next scheduled event if any exist.
   */
  async initializeAlarms() {
    var _a, _b;
    if (((_b = (_a = this.#persist) == null ? void 0 : _a.scheduledEvents) == null ? void 0 : _b.length) > 0) {
      await this.#queueSetAlarm(
        this.#persist.scheduledEvents[0].timestamp
      );
    }
  }
  /**
   * Waits for any pending alarm write operations to complete.
   */
  async waitForPendingAlarmWrites() {
    if (this.#alarmWriteQueue.runningDrainLoop) {
      await this.#alarmWriteQueue.runningDrainLoop;
    }
  }
  /**
   * Gets statistics about scheduled events.
   */
  getScheduleStats() {
    var _a;
    if (!((_a = this.#persist) == null ? void 0 : _a.scheduledEvents)) {
      return {
        totalEvents: 0,
        nextEventTime: null,
        overdueCount: 0
      };
    }
    const now = Date.now();
    const events = this.#persist.scheduledEvents;
    return {
      totalEvents: events.length,
      nextEventTime: events.length > 0 ? events[0].timestamp : null,
      overdueCount: events.filter(
        (e) => e.timestamp <= now
      ).length
    };
  }
  /**
   * Cancels a scheduled event by its ID.
   *
   * @param eventId - The ID of the event to cancel
   * @returns True if the event was found and cancelled
   */
  async cancelEvent(eventId) {
    var _a;
    if (!((_a = this.#persist) == null ? void 0 : _a.scheduledEvents)) {
      return false;
    }
    const index = this.#persist.scheduledEvents.findIndex(
      (e) => e.eventId === eventId
    );
    if (index === -1) {
      return false;
    }
    const wasFirst = index === 0;
    this.#persist.scheduledEvents.splice(index, 1);
    if (wasFirst && this.#persist.scheduledEvents.length > 0) {
      await this.#queueSetAlarm(
        this.#persist.scheduledEvents[0].timestamp
      );
    }
    this.#actor.log.info({
      msg: "cancelled scheduled event",
      eventId,
      remainingEvents: this.#persist.scheduledEvents.length
    });
    return true;
  }
  // MARK: - Private Helpers
  async #scheduleEventInner(newEvent) {
    var _a;
    this.#actor.log.info({
      msg: "scheduling event",
      eventId: newEvent.eventId,
      timestamp: newEvent.timestamp,
      action: newEvent.action
    });
    if (!((_a = this.#persist) == null ? void 0 : _a.scheduledEvents)) {
      throw new Error("Persist not initialized");
    }
    const insertIndex = this.#persist.scheduledEvents.findIndex(
      (x) => x.timestamp > newEvent.timestamp
    );
    if (insertIndex === -1) {
      this.#persist.scheduledEvents.push(newEvent);
    } else {
      this.#persist.scheduledEvents.splice(insertIndex, 0, newEvent);
    }
    if (insertIndex === 0 || this.#persist.scheduledEvents.length === 1) {
      this.#actor.log.info({
        msg: "setting alarm for new event",
        timestamp: newEvent.timestamp,
        eventCount: this.#persist.scheduledEvents.length
      });
      await this.#queueSetAlarm(newEvent.timestamp);
    }
  }
  async #executeDueEvents(events) {
    for (const event of events) {
      const span = this.#actor.startTraceSpan(
        `actor.action.${event.action}`,
        {
          "rivet.action.name": event.action,
          "rivet.action.scheduled": true,
          "rivet.schedule.event_id": event.eventId,
          "rivet.schedule.timestamp_ms": event.timestamp
        }
      );
      try {
        this.#actor.emitTraceEvent(
          "schedule.triggered",
          {
            "rivet.schedule.event_id": event.eventId,
            "rivet.schedule.action": event.action,
            "rivet.schedule.timestamp_ms": event.timestamp
          },
          span
        );
        this.#actor.log.info({
          msg: "executing scheduled event",
          eventId: event.eventId,
          timestamp: event.timestamp,
          action: event.action
        });
        const actions = this.#config.actions ?? {};
        const fn = actions[event.action];
        if (!fn) {
          throw new Error(
            `Missing action for scheduled event: ${event.action}`
          );
        }
        if (typeof fn !== "function") {
          throw new Error(
            `Scheduled event action ${event.action} is not a function (got ${typeof fn})`
          );
        }
        const args = event.args ? cbor11.decode(new Uint8Array(event.args)) : [];
        const result = this.#actor.traces.withSpan(
          span,
          () => fn.call(void 0, this.#actor.actorContext, ...args)
        );
        if (result instanceof Promise) {
          await result;
        }
        this.#actor.endTraceSpan(span, { code: "OK" });
        this.#actor.log.debug({
          msg: "scheduled event completed",
          eventId: event.eventId,
          action: event.action
        });
      } catch (error) {
        this.#actor.traces.setAttributes(span, {
          "error.message": stringifyError(error),
          "error.type": error instanceof Error ? error.name : typeof error
        });
        this.#actor.endTraceSpan(span, {
          code: "ERROR",
          message: stringifyError(error)
        });
        this.#actor.log.error({
          msg: "error executing scheduled event",
          error: stringifyError(error),
          eventId: event.eventId,
          timestamp: event.timestamp,
          action: event.action
        });
      }
    }
  }
  async #queueSetAlarm(timestamp) {
    await this.#alarmWriteQueue.enqueue(async () => {
      await this.#actorDriver.setAlarm(this.#actor, timestamp);
    });
  }
  /**
   * Gets the next scheduled event, if any.
   */
  getNextEvent() {
    var _a;
    if (!((_a = this.#persist) == null ? void 0 : _a.scheduledEvents) || this.#persist.scheduledEvents.length === 0) {
      return null;
    }
    return this.#persist.scheduledEvents[0];
  }
  /**
   * Gets all scheduled events.
   */
  getAllEvents() {
    var _a;
    return ((_a = this.#persist) == null ? void 0 : _a.scheduledEvents) || [];
  }
  /**
   * Clears all scheduled events.
   * Use with caution - this removes all pending scheduled events.
   */
  clearAllEvents() {
    var _a;
    if ((_a = this.#persist) == null ? void 0 : _a.scheduledEvents) {
      this.#persist.scheduledEvents = [];
      this.#actor.log.warn({ msg: "cleared all scheduled events" });
    }
  }
};

// src/actor/instance/state-manager.ts
import { idToStr } from "@rivetkit/engine-runner";
import onChange from "@rivetkit/on-change";
var StateManager = class {
  #actor;
  #actorDriver;
  // State tracking
  #persist;
  #persistRaw;
  #persistChanged = false;
  #isInOnStateChange = false;
  // Save management
  #persistWriteQueue = new SinglePromiseQueue();
  #lastSaveTime = 0;
  #pendingSaveTimeout;
  #pendingSaveScheduledTimestamp;
  #onPersistSavedPromise;
  // Configuration
  #config;
  // ActorConfig type
  #stateSaveInterval;
  constructor(actor, actorDriver, config) {
    this.#actor = actor;
    this.#actorDriver = actorDriver;
    this.#config = config;
    this.#stateSaveInterval = config.options.stateSaveInterval || 100;
  }
  // MARK: - Public API
  get persist() {
    return this.#persist;
  }
  get persistRaw() {
    return this.#persistRaw;
  }
  get persistChanged() {
    return this.#persistChanged;
  }
  get state() {
    this.#validateStateEnabled();
    return this.#persist.state;
  }
  set state(value) {
    this.#validateStateEnabled();
    this.#persist.state = value;
  }
  get stateEnabled() {
    return "createState" in this.#config || "state" in this.#config;
  }
  // MARK: - Initialization
  /**
   * Initializes state from persisted data or creates new state.
   */
  async initializeState(persistData) {
    if (!persistData.hasInitialized) {
      let stateData;
      if (this.stateEnabled) {
        this.#actor.rLog.info({ msg: "actor state initializing" });
        if ("createState" in this.#config) {
          stateData = await this.#actor.runInTraceSpan(
            "actor.createState",
            void 0,
            () => this.#config.createState(
              this.#actor.actorContext,
              persistData.input
            )
          );
        } else if ("state" in this.#config) {
          stateData = structuredClone(this.#config.state);
        } else {
          throw new Error(
            "Both 'createState' or 'state' were not defined"
          );
        }
      } else {
        this.#actor.rLog.debug({ msg: "state not enabled" });
      }
      persistData.state = stateData;
      persistData.hasInitialized = true;
      const bareData = convertActorToBarePersisted(persistData);
      await this.#actorDriver.kvBatchPut(this.#actor.id, [
        [
          KEYS.PERSIST_DATA,
          ACTOR_VERSIONED.serializeWithEmbeddedVersion(
            bareData,
            CURRENT_VERSION2
          )
        ]
      ]);
    }
    this.initPersistProxy(persistData);
  }
  /**
   * Creates proxy for persist object that handles automatic state change detection.
   */
  initPersistProxy(target) {
    this.#persistRaw = target;
    if (target === null || typeof target !== "object") {
      let invalidPath = "";
      if (!isCborSerializable(
        target,
        (path) => {
          invalidPath = path;
        },
        ""
      )) {
        throw new InvalidStateType({ path: invalidPath });
      }
      return target;
    }
    if (this.#persist) {
      onChange.unsubscribe(this.#persist);
    }
    this.#persist = onChange(
      target,
      (path, value, _previousValue, _applyData) => {
        this.#handleStateChange(path, value);
      },
      { ignoreDetached: true }
    );
  }
  // MARK: - State Persistence
  /**
   * Forces the state to get saved.
   */
  async saveState(opts) {
    var _a;
    this.#actor.assertReady(opts.allowStoppingState);
    if (this.#persistChanged) {
      if (opts.immediate) {
        await this.#savePersistInner();
      } else {
        if (!this.#onPersistSavedPromise) {
          this.#onPersistSavedPromise = promiseWithResolvers();
        }
        this.savePersistThrottled(opts.maxWait);
        await ((_a = this.#onPersistSavedPromise) == null ? void 0 : _a.promise);
      }
    }
  }
  /**
   * Throttled save state method. Used to write to KV at a reasonable cadence.
   *
   * Passing a maxWait will override the stateSaveInterval with the min
   * between that and the maxWait.
   */
  savePersistThrottled(maxWait) {
    const now = Date.now();
    const timeSinceLastSave = now - this.#lastSaveTime;
    let saveDelay = Math.max(
      0,
      this.#stateSaveInterval - timeSinceLastSave
    );
    if (maxWait !== void 0) {
      saveDelay = Math.min(saveDelay, maxWait);
    }
    if (this.#pendingSaveTimeout !== void 0 && this.#pendingSaveScheduledTimestamp !== void 0) {
      const newScheduledTimestamp = now + saveDelay;
      if (newScheduledTimestamp < this.#pendingSaveScheduledTimestamp) {
        clearTimeout(this.#pendingSaveTimeout);
        this.#pendingSaveTimeout = void 0;
        this.#pendingSaveScheduledTimestamp = void 0;
      } else {
        return;
      }
    }
    if (saveDelay > 0) {
      this.#pendingSaveScheduledTimestamp = now + saveDelay;
      this.#pendingSaveTimeout = setTimeout(() => {
        this.#pendingSaveTimeout = void 0;
        this.#pendingSaveScheduledTimestamp = void 0;
        this.#savePersistInner().catch((error) => {
          this.#actor.rLog.error({
            msg: "error saving persist data in scheduled save",
            error: stringifyError(error)
          });
        });
      }, saveDelay);
    } else {
      this.#savePersistInner().catch((error) => {
        this.#actor.rLog.error({
          msg: "error saving persist data immediately",
          error: stringifyError(error)
        });
      });
    }
  }
  /**
   * Clears any pending save timeout.
   */
  clearPendingSaveTimeout() {
    if (this.#pendingSaveTimeout) {
      clearTimeout(this.#pendingSaveTimeout);
      this.#pendingSaveTimeout = void 0;
      this.#pendingSaveScheduledTimestamp = void 0;
    }
  }
  /**
   * Waits for any pending write operations to complete.
   */
  async waitForPendingWrites() {
    if (this.#persistWriteQueue.runningDrainLoop) {
      await this.#persistWriteQueue.runningDrainLoop;
    }
  }
  // MARK: - Private Helpers
  #validateStateEnabled() {
    if (!this.stateEnabled) {
      throw new StateNotEnabled();
    }
  }
  #handleStateChange(path, value) {
    const actorStatePath = isStatePath(path);
    const connStatePath = isConnStatePath(path);
    if (actorStatePath || connStatePath) {
      let invalidPath = "";
      if (!isCborSerializable(
        value,
        (invalidPathPart) => {
          invalidPath = invalidPathPart;
        },
        ""
      )) {
        throw new InvalidStateType({
          path: path + (invalidPath ? `.${invalidPath}` : "")
        });
      }
    }
    this.#actor.rLog.debug({
      msg: "onChange triggered, setting persistChanged=true",
      path
    });
    this.#persistChanged = true;
    if (actorStatePath) {
      this.#actor.inspector.emitter.emit(
        "stateUpdated",
        this.#persist.state
      );
    }
    if (actorStatePath && this.#config.onStateChange && this.#actor.isReady() && !this.#isInOnStateChange) {
      const span = this.#actor.startTraceSpan("actor.onStateChange", {
        "rivet.state.path": path
      });
      try {
        this.#isInOnStateChange = true;
        this.#actor.traces.withSpan(
          span,
          () => this.#config.onStateChange(
            this.#actor.actorContext,
            this.#persistRaw.state
          )
        );
        this.#actor.endTraceSpan(span, { code: "OK" });
      } catch (error) {
        this.#actor.endTraceSpan(span, {
          code: "ERROR",
          message: stringifyError(error)
        });
        this.#actor.rLog.error({
          msg: "error in `_onStateChange`",
          error: stringifyError(error)
        });
      } finally {
        this.#isInOnStateChange = false;
      }
    }
  }
  async #savePersistInner() {
    var _a, _b;
    this.#actor.rLog.info({
      msg: "savePersistInner called",
      persistChanged: this.#persistChanged,
      connsWithPersistChangedSize: this.#actor.connectionManager.connsWithPersistChanged.size,
      connsWithPersistChangedIds: Array.from(
        this.#actor.connectionManager.connsWithPersistChanged
      )
    });
    try {
      this.#lastSaveTime = Date.now();
      const hasChanges = this.#persistChanged || this.#actor.connectionManager.connsWithPersistChanged.size > 0;
      if (hasChanges) {
        await this.#persistWriteQueue.enqueue(async () => {
          this.#actor.rLog.debug({
            msg: "saving persist",
            actorChanged: this.#persistChanged,
            connectionsChanged: this.#actor.connectionManager.connsWithPersistChanged.size
          });
          const entries = [];
          if (this.#persistChanged) {
            this.#persistChanged = false;
            const bareData = convertActorToBarePersisted(
              this.#persistRaw
            );
            entries.push([
              KEYS.PERSIST_DATA,
              ACTOR_VERSIONED.serializeWithEmbeddedVersion(
                bareData,
                CURRENT_VERSION2
              )
            ]);
          }
          const connections = [];
          for (const connId of this.#actor.connectionManager.connsWithPersistChanged) {
            const conn = this.#actor.conns.get(connId);
            if (!conn) {
              this.#actor.rLog.warn({
                msg: "connection not found in conns map",
                connId
              });
              continue;
            }
            const connStateManager = conn[CONN_STATE_MANAGER_SYMBOL];
            const hibernatableDataRaw = connStateManager.hibernatableDataRaw;
            if (!hibernatableDataRaw) {
              this.#actor.log.warn({
                msg: "missing raw hibernatable data for conn in getChangedConnectionsData",
                connId: conn.id
              });
              continue;
            }
            this.#actor.rLog.info({
              msg: "persisting connection",
              connId,
              gatewayId: idToStr(hibernatableDataRaw.requestId),
              requestId: idToStr(hibernatableDataRaw.requestId),
              serverMessageIndex: hibernatableDataRaw.serverMessageIndex,
              clientMessageIndex: hibernatableDataRaw.clientMessageIndex,
              hasState: hibernatableDataRaw.state !== void 0
            });
            const bareData = convertConnToBarePersistedConn(
              hibernatableDataRaw
            );
            const connData = CONN_VERSIONED.serializeWithEmbeddedVersion(
              bareData,
              CURRENT_VERSION2
            );
            entries.push([makeConnKey(connId), connData]);
            connections.push(conn);
          }
          this.#actor.rLog.info({
            msg: "prepared entries for kvBatchPut",
            totalEntries: entries.length,
            connectionEntries: connections.length,
            connectionIds: connections.map((c) => c.id)
          });
          if (this.#actorDriver.onBeforePersistConn) {
            for (const conn of connections) {
              this.#actorDriver.onBeforePersistConn(conn);
            }
          }
          this.#actor.connectionManager.clearConnWithPersistChanged();
          this.#actor.rLog.info({
            msg: "calling kvBatchPut",
            actorId: this.#actor.id,
            entriesCount: entries.length
          });
          await this.#actorDriver.kvBatchPut(this.#actor.id, entries);
          this.#actor.rLog.info({
            msg: "kvBatchPut completed successfully"
          });
          if (this.#actorDriver.onAfterPersistConn) {
            for (const conn of connections) {
              this.#actorDriver.onAfterPersistConn(conn);
            }
          }
          this.#actor.rLog.debug({ msg: "persist saved" });
        });
      } else {
        this.#actor.rLog.info({
          msg: "savePersistInner skipped - no changes"
        });
      }
      (_a = this.#onPersistSavedPromise) == null ? void 0 : _a.resolve();
    } catch (error) {
      this.#actor.rLog.error({
        msg: "error saving persist",
        error: stringifyError(error)
      });
      (_b = this.#onPersistSavedPromise) == null ? void 0 : _b.reject(error);
      throw error;
    }
  }
};

// src/actor/instance/mod.ts
var CanSleep = /* @__PURE__ */ ((CanSleep2) => {
  CanSleep2[CanSleep2["Yes"] = 0] = "Yes";
  CanSleep2[CanSleep2["NotReady"] = 1] = "NotReady";
  CanSleep2[CanSleep2["NotStarted"] = 2] = "NotStarted";
  CanSleep2[CanSleep2["ActiveConns"] = 3] = "ActiveConns";
  CanSleep2[CanSleep2["ActiveHonoHttpRequests"] = 4] = "ActiveHonoHttpRequests";
  CanSleep2[CanSleep2["ActiveKeepAwake"] = 5] = "ActiveKeepAwake";
  return CanSleep2;
})(CanSleep || {});
var ActorInstance = class {
  // MARK: - Core Properties
  actorContext;
  #config;
  driver;
  #inlineClient;
  #actorId;
  #name;
  #key;
  #actorKeyString;
  #region;
  // MARK: - Managers
  connectionManager;
  stateManager;
  eventManager;
  #scheduleManager;
  queueManager;
  // MARK: - Logging
  #log;
  #rLog;
  // MARK: - Lifecycle State
  /**
   * If the core actor initiation has set up.
   *
   * Almost all actions on this actor will throw an error if false.
   **/
  #ready = false;
  /**
   * If the actor has fully started.
   *
   * The only purpose of this is to prevent sleeping until started.
   */
  #started = false;
  #sleepCalled = false;
  #destroyCalled = false;
  #stopCalled = false;
  #sleepTimeout;
  #abortController = new AbortController();
  // MARK: - Variables & Database
  #vars;
  #db;
  // MARK: - Background Tasks
  #backgroundPromises = [];
  #runPromise;
  // MARK: - HTTP/WebSocket Tracking
  #activeHonoHttpRequests = 0;
  #activeKeepAwakeCount = 0;
  // MARK: - Deprecated (kept for compatibility)
  #schedule;
  // MARK: - Inspector
  #inspectorToken;
  #inspector = new ActorInspector(this);
  // MARK: - Tracing
  #traces;
  // MARK: - Constructor
  constructor(config) {
    this.#config = config;
    this.actorContext = new ActorContext(this);
  }
  // MARK: - Public Getters
  get log() {
    invariant7(this.#log, "log not configured");
    return this.#log;
  }
  get rLog() {
    invariant7(this.#rLog, "log not configured");
    return this.#rLog;
  }
  get isStopping() {
    return this.#stopCalled;
  }
  get id() {
    return this.#actorId;
  }
  get name() {
    return this.#name;
  }
  get key() {
    return this.#key;
  }
  get region() {
    return this.#region;
  }
  get inlineClient() {
    return this.#inlineClient;
  }
  get inspector() {
    return this.#inspector;
  }
  get traces() {
    return this.#traces;
  }
  get inspectorToken() {
    return this.#inspectorToken;
  }
  // MARK: - Tracing
  getCurrentTraceSpan() {
    return this.#traces.getCurrentSpan();
  }
  startTraceSpan(name, attributes) {
    return this.#traces.startSpan(name, {
      parent: this.#traces.getCurrentSpan() ?? void 0,
      attributes: this.#traceAttributes(attributes)
    });
  }
  endTraceSpan(handle, status) {
    this.#traces.endSpan(
      handle,
      status ? { status } : void 0
    );
  }
  async runInTraceSpan(name, attributes, fn) {
    const span = this.startTraceSpan(name, attributes);
    try {
      const result = this.#traces.withSpan(span, fn);
      const resolved = result instanceof Promise ? await result : result;
      this.#traces.endSpan(span, {
        status: { code: "OK" }
      });
      return resolved;
    } catch (error) {
      this.#traces.endSpan(span, {
        status: {
          code: "ERROR",
          message: stringifyError(error)
        }
      });
      throw error;
    }
  }
  emitTraceEvent(name, attributes, handle) {
    const span = handle ?? this.#traces.getCurrentSpan();
    if (!span) {
      return;
    }
    this.#traces.emitEvent(span, name, {
      attributes: this.#traceAttributes(attributes),
      timeUnixMs: Date.now()
    });
  }
  get conns() {
    return this.connectionManager.connections;
  }
  get schedule() {
    return this.#schedule;
  }
  get abortSignal() {
    return this.#abortController.signal;
  }
  get actions() {
    return Object.keys(this.#config.actions ?? {});
  }
  get config() {
    return this.#config;
  }
  // MARK: - State Access
  get persist() {
    return this.stateManager.persist;
  }
  get state() {
    return this.stateManager.state;
  }
  set state(value) {
    this.stateManager.state = value;
  }
  get stateEnabled() {
    return this.stateManager.stateEnabled;
  }
  get connStateEnabled() {
    return "createConnState" in this.#config || "connState" in this.#config;
  }
  // MARK: - Variables & Database
  get vars() {
    this.#validateVarsEnabled();
    invariant7(this.#vars !== void 0, "vars not enabled");
    return this.#vars;
  }
  get db() {
    if (!this.#db) {
      throw new DatabaseNotEnabled();
    }
    return this.#db;
  }
  // MARK: - Initialization
  async start(actorDriver, inlineClient, actorId, name, key, region) {
    var _a, _b;
    this.driver = actorDriver;
    this.#inlineClient = inlineClient;
    this.#actorId = actorId;
    this.#name = name;
    this.#key = key;
    this.#actorKeyString = serializeActorKey(this.#key);
    this.#region = region;
    this.#initializeTraces();
    this.#initializeLogging();
    this.connectionManager = new ConnectionManager(this);
    this.stateManager = new StateManager(this, actorDriver, this.#config);
    this.eventManager = new EventManager(this);
    this.queueManager = new QueueManager(this, actorDriver);
    this.#scheduleManager = new ScheduleManager(
      this,
      actorDriver,
      this.#config
    );
    this.#schedule = new Schedule(this);
    await this.#loadState();
    await this.queueManager.initialize();
    await this.#initializeInspectorToken();
    if (this.#varsEnabled) {
      await this.#initializeVars();
    }
    await this.#callOnStart();
    await this.#setupDatabase();
    await this.#scheduleManager.initializeAlarms();
    this.#ready = true;
    await ((_b = (_a = this.driver).onBeforeActorStart) == null ? void 0 : _b.call(_a, this));
    this.#started = true;
    this.#rLog.info({ msg: "actor started" });
    this.resetSleepTimer();
    this.#startRunHandler();
    await this.onAlarm();
  }
  // MARK: - Ready Check
  isReady() {
    return this.#ready;
  }
  assertReady(allowStoppingState = false) {
    if (!this.#ready) throw new InternalError("Actor not ready");
    if (!allowStoppingState && this.#stopCalled)
      throw new InternalError("Actor is stopping");
  }
  // MARK: - Stop
  async onStop(mode) {
    if (this.#stopCalled) {
      this.#rLog.warn({ msg: "already stopping actor" });
      return;
    }
    this.#stopCalled = true;
    this.#rLog.info({
      msg: "setting stopCalled=true",
      mode
    });
    if (this.#sleepTimeout) {
      clearTimeout(this.#sleepTimeout);
      this.#sleepTimeout = void 0;
    }
    try {
      this.#abortController.abort();
    } catch {
    }
    await this.#waitForRunHandler(this.#config.options.runStopTimeout);
    if (mode === "sleep") {
      await this.#callOnSleep();
    } else if (mode === "destroy") {
      await this.#callOnDestroy();
    } else {
      assertUnreachable2(mode);
    }
    await this.#disconnectConnections();
    await this.#waitBackgroundPromises(
      this.#config.options.waitUntilTimeout
    );
    this.#rLog.info({ msg: "clearing pending save timeouts" });
    this.stateManager.clearPendingSaveTimeout();
    this.#rLog.info({ msg: "saving state immediately" });
    await this.stateManager.saveState({
      immediate: true,
      allowStoppingState: true
    });
    await this.stateManager.waitForPendingWrites();
    await this.#scheduleManager.waitForPendingAlarmWrites();
  }
  // MARK: - Sleep
  startSleep() {
    var _a;
    if (this.#stopCalled || this.#destroyCalled) {
      this.#rLog.debug({
        msg: "cannot call startSleep if actor already stopping"
      });
      return;
    }
    if (this.#sleepCalled) {
      this.#rLog.warn({
        msg: "cannot call startSleep twice, actor already sleeping"
      });
      return;
    }
    this.#sleepCalled = true;
    const sleep = (_a = this.driver.startSleep) == null ? void 0 : _a.bind(this.driver, this.#actorId);
    invariant7(this.#sleepingSupported, "sleeping not supported");
    invariant7(sleep, "no sleep on driver");
    this.#rLog.info({ msg: "actor sleeping" });
    setImmediate(() => {
      sleep();
    });
  }
  // MARK: - Destroy
  startDestroy() {
    if (this.#stopCalled || this.#sleepCalled) {
      this.#rLog.debug({
        msg: "cannot call startDestroy if actor already stopping or sleeping"
      });
      return;
    }
    if (this.#destroyCalled) {
      this.#rLog.warn({
        msg: "cannot call startDestroy twice, actor already destroying"
      });
      return;
    }
    this.#destroyCalled = true;
    const destroy = this.driver.startDestroy.bind(
      this.driver,
      this.#actorId
    );
    this.#rLog.info({ msg: "actor destroying" });
    setImmediate(() => {
      destroy();
    });
  }
  // MARK: - HTTP Request Tracking
  beginHonoHttpRequest() {
    this.#activeHonoHttpRequests++;
    this.resetSleepTimer();
  }
  endHonoHttpRequest() {
    this.#activeHonoHttpRequests--;
    if (this.#activeHonoHttpRequests < 0) {
      this.#activeHonoHttpRequests = 0;
      this.#rLog.warn({
        msg: "active hono requests went below 0, this is a RivetKit bug",
        ...EXTRA_ERROR_LOG
      });
    }
    this.resetSleepTimer();
  }
  // MARK: - Message Processing
  async processMessage(message, conn) {
    await processMessage(message, this, conn, {
      onExecuteAction: async (ctx, name, args) => {
        return await this.executeAction(ctx, name, args);
      },
      onSubscribe: async (eventName, conn2) => {
        this.eventManager.addSubscription(eventName, conn2, false);
      },
      onUnsubscribe: async (eventName, conn2) => {
        this.eventManager.removeSubscription(eventName, conn2, false);
      }
    });
  }
  // MARK: - Action Execution
  async executeAction(ctx, actionName, args) {
    this.assertReady();
    const actions = this.#config.actions ?? {};
    if (!(actionName in actions)) {
      this.#rLog.warn({ msg: "action does not exist", actionName });
      throw new ActionNotFound(actionName);
    }
    const actionFunction = actions[actionName];
    if (typeof actionFunction !== "function") {
      this.#rLog.warn({
        msg: "action is not a function",
        actionName,
        type: typeof actionFunction
      });
      throw new ActionNotFound(actionName);
    }
    const actionSpan = this.startTraceSpan(
      `actor.action.${actionName}`,
      {
        "rivet.action.name": actionName
      }
    );
    let spanEnded = false;
    try {
      return await this.#traces.withSpan(actionSpan, async () => {
        this.#rLog.debug({
          msg: "executing action",
          actionName,
          args
        });
        const outputOrPromise = actionFunction.call(
          void 0,
          ctx,
          ...args
        );
        let output;
        if (outputOrPromise instanceof Promise) {
          output = await deadline(
            outputOrPromise,
            this.#config.options.actionTimeout
          );
        } else {
          output = outputOrPromise;
        }
        if (this.#config.onBeforeActionResponse) {
          try {
            output = await this.runInTraceSpan(
              "actor.onBeforeActionResponse",
              { "rivet.action.name": actionName },
              () => this.#config.onBeforeActionResponse(
                this.actorContext,
                actionName,
                args,
                output
              )
            );
          } catch (error) {
            this.#rLog.error({
              msg: "error in `onBeforeActionResponse`",
              error: stringifyError(error)
            });
          }
        }
        return output;
      });
    } catch (error) {
      const isTimeout = error instanceof DeadlineError;
      const message = isTimeout ? "ActionTimedOut" : stringifyError(error);
      this.#traces.setAttributes(actionSpan, {
        "error.message": message,
        "error.type": error instanceof Error ? error.name : typeof error
      });
      this.#traces.endSpan(actionSpan, {
        status: { code: "ERROR", message }
      });
      spanEnded = true;
      if (isTimeout) {
        throw new ActionTimedOut();
      }
      this.#rLog.error({
        msg: "action error",
        actionName,
        error: stringifyError(error)
      });
      throw error;
    } finally {
      if (!spanEnded && actionSpan.isActive()) {
        this.#traces.endSpan(actionSpan, {
          status: { code: "OK" }
        });
      }
      this.stateManager.savePersistThrottled();
    }
  }
  // MARK: - HTTP/WebSocket Handlers
  async handleRawRequest(conn, request) {
    this.assertReady();
    if (!this.#config.onRequest) {
      throw new RequestHandlerNotDefined();
    }
    const onRequest = this.#config.onRequest;
    return await this.runInTraceSpan(
      "actor.onRequest",
      {
        "http.method": request.method,
        "http.url": request.url,
        "rivet.conn.id": conn.id
      },
      async () => {
        try {
          const ctx = new RequestContext(this, conn, request);
          const response = await onRequest(ctx, request);
          if (!response) {
            throw new InvalidRequestHandlerResponse();
          }
          return response;
        } catch (error) {
          this.#rLog.error({
            msg: "onRequest error",
            error: stringifyError(error)
          });
          throw error;
        } finally {
          this.stateManager.savePersistThrottled();
        }
      }
    );
  }
  handleRawWebSocket(conn, websocket, request) {
    this.assertReady();
    if (!this.#config.onWebSocket) {
      throw new InternalError("onWebSocket handler not defined");
    }
    const span = this.startTraceSpan("actor.onWebSocket", {
      "http.url": request == null ? void 0 : request.url,
      "rivet.conn.id": conn.id
    });
    let spanEnded = false;
    try {
      this.resetSleepTimer();
      const ctx = new WebSocketContext(this, conn, request);
      const voidOrPromise = this.#traces.withSpan(
        span,
        () => this.#config.onWebSocket(ctx, websocket)
      );
      if (voidOrPromise instanceof Promise) {
        voidOrPromise.then(() => {
          if (!spanEnded) {
            this.endTraceSpan(span, { code: "OK" });
            spanEnded = true;
          }
        }).catch((error) => {
          if (!spanEnded) {
            this.endTraceSpan(span, {
              code: "ERROR",
              message: stringifyError(error)
            });
            spanEnded = true;
          }
          this.#rLog.error({
            msg: "onWebSocket error",
            error: stringifyError(error)
          });
        }).finally(() => {
          this.stateManager.savePersistThrottled();
        });
      } else {
        if (!spanEnded) {
          this.endTraceSpan(span, { code: "OK" });
          spanEnded = true;
        }
        this.stateManager.savePersistThrottled();
      }
    } catch (error) {
      if (!spanEnded) {
        this.endTraceSpan(span, {
          code: "ERROR",
          message: stringifyError(error)
        });
        spanEnded = true;
      }
      this.#rLog.error({
        msg: "onWebSocket error",
        error: stringifyError(error)
      });
      throw error;
    }
  }
  // MARK: - Scheduling
  async scheduleEvent(timestamp, action, args) {
    await this.#scheduleManager.scheduleEvent(timestamp, action, args);
  }
  async onAlarm() {
    this.resetSleepTimer();
    await this.#scheduleManager.onAlarm();
  }
  // MARK: - Background Tasks
  waitUntil(promise) {
    this.assertReady();
    const nonfailablePromise = promise.then(() => {
      this.#rLog.debug({ msg: "wait until promise complete" });
    }).catch((error) => {
      this.#rLog.error({
        msg: "wait until promise failed",
        error: stringifyError(error)
      });
    });
    this.#backgroundPromises.push(nonfailablePromise);
  }
  /**
   * Prevents the actor from sleeping until promise is complete.
   * Returns the resolved value and resets sleep timer on completion.
   * Unlike waitUntil, errors are propagated to the caller.
   */
  async keepAwake(promise) {
    this.assertReady();
    this.#activeKeepAwakeCount++;
    this.resetSleepTimer();
    try {
      return await promise;
    } finally {
      this.#activeKeepAwakeCount--;
      if (this.#activeKeepAwakeCount < 0) {
        this.#activeKeepAwakeCount = 0;
        this.#rLog.warn({
          msg: "active keep awake count went below 0, this is a RivetKit bug",
          ...EXTRA_ERROR_LOG
        });
      }
      this.resetSleepTimer();
    }
  }
  // MARK: - Private Helper Methods
  #initializeTraces() {
    this.#traces = createTraces({
      driver: new ActorTracesDriver(this.driver, this.#actorId)
    });
  }
  #traceAttributes(attributes) {
    return {
      "rivet.actor.id": this.#actorId,
      "rivet.actor.name": this.#name,
      "rivet.actor.key": this.#actorKeyString,
      "rivet.actor.region": this.#region,
      ...attributes ?? {}
    };
  }
  #patchLoggerForTraces(logger4) {
    const levels = ["trace", "debug", "info", "warn", "error", "fatal"];
    for (const level of levels) {
      const original = logger4[level].bind(logger4);
      logger4[level] = ((...args) => {
        this.#emitLogEvent(level, args);
        return original(...args);
      });
    }
  }
  #emitLogEvent(level, args) {
    const span = this.#traces.getCurrentSpan();
    if (!span || !span.isActive()) {
      return;
    }
    let message;
    if (args.length >= 2) {
      message = String(args[1]);
    } else if (args.length === 1) {
      const [value] = args;
      if (typeof value === "string") {
        message = value;
      } else if (typeof value === "number" || typeof value === "boolean") {
        message = String(value);
      } else if (value && typeof value === "object") {
        const maybeMsg = value.msg;
        if (maybeMsg !== void 0) {
          message = String(maybeMsg);
        }
      }
    }
    this.#traces.emitEvent(span, "log", {
      attributes: this.#traceAttributes({
        "log.level": level,
        ...message ? { "log.message": message } : {}
      }),
      timeUnixMs: Date.now()
    });
  }
  #initializeLogging() {
    var _a, _b;
    const logParams = {
      actor: this.#name,
      key: this.#actorKeyString,
      actorId: this.#actorId
    };
    const extraLogParams = (_b = (_a = this.driver).getExtraActorLogParams) == null ? void 0 : _b.call(_a);
    if (extraLogParams) Object.assign(logParams, extraLogParams);
    this.#log = getBaseLogger().child(
      Object.assign(
        getIncludeTarget() ? { target: "actor" } : {},
        logParams
      )
    );
    this.#rLog = getBaseLogger().child(
      Object.assign(
        getIncludeTarget() ? { target: "actor-runtime" } : {},
        logParams
      )
    );
    this.#patchLoggerForTraces(this.#log);
    this.#patchLoggerForTraces(this.#rLog);
  }
  async #loadState() {
    const [persistDataBuffer] = await this.driver.kvBatchGet(
      this.#actorId,
      [KEYS.PERSIST_DATA]
    );
    invariant7(
      persistDataBuffer !== null,
      "persist data has not been set, it should be set when initialized"
    );
    const bareData = ACTOR_VERSIONED.deserializeWithEmbeddedVersion(persistDataBuffer);
    const persistData = convertActorFromBarePersisted(bareData);
    if (persistData.hasInitialized) {
      await this.#restoreExistingActor(persistData);
    } else {
      await this.#createNewActor(persistData);
    }
    this.#scheduleManager.setPersist(this.stateManager.persist);
  }
  async #createNewActor(persistData) {
    this.#rLog.info({ msg: "actor creating" });
    await this.stateManager.initializeState(persistData);
    if (this.#config.onCreate) {
      const onCreate = this.#config.onCreate;
      await this.runInTraceSpan(
        "actor.onCreate",
        void 0,
        () => onCreate(this.actorContext, persistData.input)
      );
    }
  }
  async #restoreExistingActor(persistData) {
    const connEntries = await this.driver.kvListPrefix(
      this.#actorId,
      KEYS.CONN_PREFIX
    );
    const connections = [];
    for (const [_key, value] of connEntries) {
      try {
        const bareData = CONN_VERSIONED.deserializeWithEmbeddedVersion(
          new Uint8Array(value)
        );
        const conn = convertConnFromBarePersistedConn(bareData);
        connections.push(conn);
      } catch (error) {
        this.#rLog.error({
          msg: "failed to decode connection",
          error: stringifyError(error)
        });
      }
    }
    this.#rLog.info({
      msg: "actor restoring",
      connections: connections.length
    });
    this.stateManager.initPersistProxy(persistData);
    this.connectionManager.restoreConnections(connections);
  }
  async #initializeInspectorToken() {
    const [tokenBuffer] = await this.driver.kvBatchGet(this.#actorId, [
      KEYS.INSPECTOR_TOKEN
    ]);
    if (tokenBuffer !== null) {
      const decoder = new TextDecoder();
      this.#inspectorToken = decoder.decode(tokenBuffer);
      this.#rLog.debug({ msg: "loaded existing inspector token" });
    } else {
      this.#inspectorToken = generateSecureToken();
      const tokenBytes = new TextEncoder().encode(this.#inspectorToken);
      await this.driver.kvBatchPut(this.#actorId, [
        [KEYS.INSPECTOR_TOKEN, tokenBytes]
      ]);
      this.#rLog.debug({ msg: "generated new inspector token" });
    }
  }
  async #initializeVars() {
    let vars;
    if ("createVars" in this.#config) {
      const createVars = this.#config.createVars;
      vars = await this.runInTraceSpan("actor.createVars", void 0, () => {
        const dataOrPromise = createVars(
          this.actorContext,
          this.driver.getContext(this.#actorId)
        );
        if (dataOrPromise instanceof Promise) {
          return deadline(
            dataOrPromise,
            this.#config.options.createVarsTimeout
          );
        }
        return dataOrPromise;
      });
    } else if ("vars" in this.#config) {
      vars = structuredClone(this.#config.vars);
    } else {
      throw new Error(
        "Could not create variables from 'createVars' or 'vars'"
      );
    }
    this.#vars = vars;
  }
  async #callOnStart() {
    this.#rLog.info({ msg: "actor starting" });
    if (this.#config.onWake) {
      const onWake = this.#config.onWake;
      await this.runInTraceSpan(
        "actor.onWake",
        void 0,
        () => onWake(this.actorContext)
      );
    }
  }
  async #callOnSleep() {
    if (this.#config.onSleep) {
      const onSleep = this.#config.onSleep;
      try {
        this.#rLog.debug({ msg: "calling onSleep" });
        await this.runInTraceSpan("actor.onSleep", void 0, async () => {
          const result = onSleep(this.actorContext);
          if (result instanceof Promise) {
            await deadline(
              result,
              this.#config.options.onSleepTimeout
            );
          }
        });
        this.#rLog.debug({ msg: "onSleep completed" });
      } catch (error) {
        if (error instanceof DeadlineError) {
          this.#rLog.error({ msg: "onSleep timed out" });
        } else {
          this.#rLog.error({
            msg: "error in onSleep",
            error: stringifyError(error)
          });
        }
      }
    }
  }
  async #callOnDestroy() {
    if (this.#config.onDestroy) {
      const onDestroy = this.#config.onDestroy;
      try {
        this.#rLog.debug({ msg: "calling onDestroy" });
        await this.runInTraceSpan("actor.onDestroy", void 0, async () => {
          const result = onDestroy(this.actorContext);
          if (result instanceof Promise) {
            await deadline(
              result,
              this.#config.options.onDestroyTimeout
            );
          }
        });
        this.#rLog.debug({ msg: "onDestroy completed" });
      } catch (error) {
        if (error instanceof DeadlineError) {
          this.#rLog.error({ msg: "onDestroy timed out" });
        } else {
          this.#rLog.error({
            msg: "error in onDestroy",
            error: stringifyError(error)
          });
        }
      }
    }
  }
  #startRunHandler() {
    const runFn = getRunFunction(this.#config.run);
    if (!runFn) return;
    this.#rLog.debug({ msg: "starting run handler" });
    const runSpan = this.startTraceSpan("actor.run");
    const runResult = this.#traces.withSpan(
      runSpan,
      () => runFn(this.actorContext)
    );
    if (runResult instanceof Promise) {
      this.#runPromise = runResult.then(() => {
        this.emitTraceEvent(
          "actor.crash",
          { "rivet.actor.reason": "run_exited" },
          runSpan
        );
        this.endTraceSpan(runSpan, {
          code: "ERROR",
          message: "run exited unexpectedly"
        });
        this.#rLog.warn({
          msg: "run handler exited unexpectedly, crashing actor to reschedule"
        });
        this.startDestroy();
      }).catch((error) => {
        this.emitTraceEvent(
          "actor.crash",
          {
            "rivet.actor.reason": "run_error",
            "error.message": stringifyError(error)
          },
          runSpan
        );
        this.endTraceSpan(runSpan, {
          code: "ERROR",
          message: stringifyError(error)
        });
        this.#rLog.error({
          msg: "run handler threw error, crashing actor to reschedule",
          error: stringifyError(error)
        });
        this.startDestroy();
      });
    } else if (runSpan.isActive()) {
      this.endTraceSpan(runSpan, { code: "OK" });
    }
  }
  async #waitForRunHandler(timeoutMs) {
    if (!this.#runPromise) {
      return;
    }
    this.#rLog.debug({ msg: "waiting for run handler to complete" });
    const timedOut = await Promise.race([
      this.#runPromise.then(() => false).catch(() => false),
      new Promise(
        (resolve) => setTimeout(() => resolve(true), timeoutMs)
      )
    ]);
    if (timedOut) {
      this.#rLog.warn({
        msg: "run handler did not complete in time, it may have leaked - ensure you use the abort signal (c.abortSignal) to exit gracefully",
        timeoutMs
      });
    } else {
      this.#rLog.debug({ msg: "run handler completed" });
    }
  }
  async #setupDatabase() {
    var _a, _b;
    if ("db" in this.#config && this.#config.db) {
      const client = await this.#config.db.createClient({
        getDatabase: () => this.driver.getDatabase(this.#actorId)
      });
      this.#rLog.info({ msg: "database migration starting" });
      await ((_b = (_a = this.#config.db).onMigrate) == null ? void 0 : _b.call(_a, client));
      this.#rLog.info({ msg: "database migration complete" });
      this.#db = client;
    }
  }
  async #disconnectConnections() {
    const promises = [];
    this.#rLog.debug({
      msg: "disconnecting connections on actor stop",
      totalConns: this.connectionManager.connections.size
    });
    for (const connection of this.connectionManager.connections.values()) {
      this.#rLog.debug({
        msg: "checking connection for disconnect",
        connId: connection.id,
        isHibernatable: connection.isHibernatable
      });
      if (!connection.isHibernatable) {
        this.#rLog.debug({
          msg: "disconnecting non-hibernatable connection on actor stop",
          connId: connection.id
        });
        promises.push(connection.disconnect());
      } else {
        this.#rLog.debug({
          msg: "preserving hibernatable connection on actor stop",
          connId: connection.id
        });
      }
    }
    const res = await Promise.race([
      Promise.all(promises).then(() => false),
      new Promise(
        (res2) => globalThis.setTimeout(() => res2(true), 1500)
      )
    ]);
    if (res) {
      this.#rLog.warn({
        msg: "timed out waiting for connections to close, shutting down anyway"
      });
    }
  }
  async #waitBackgroundPromises(timeoutMs) {
    const pending = this.#backgroundPromises;
    if (pending.length === 0) {
      this.#rLog.debug({ msg: "no background promises" });
      return;
    }
    const timedOut = await Promise.race([
      Promise.allSettled(pending).then(() => false),
      new Promise(
        (resolve) => setTimeout(() => resolve(true), timeoutMs)
      )
    ]);
    if (timedOut) {
      this.#rLog.error({
        msg: "timed out waiting for background tasks",
        count: pending.length,
        timeoutMs
      });
    } else {
      this.#rLog.debug({ msg: "background promises finished" });
    }
  }
  resetSleepTimer() {
    if (this.#config.options.noSleep || !this.#sleepingSupported) return;
    if (this.#stopCalled) return;
    const canSleep = this.#canSleep();
    this.#rLog.debug({
      msg: "resetting sleep timer",
      canSleep: CanSleep[canSleep],
      existingTimeout: !!this.#sleepTimeout,
      timeout: this.#config.options.sleepTimeout
    });
    if (this.#sleepTimeout) {
      clearTimeout(this.#sleepTimeout);
      this.#sleepTimeout = void 0;
    }
    if (this.#sleepCalled) return;
    if (canSleep === 0 /* Yes */) {
      this.#sleepTimeout = setTimeout(() => {
        this.startSleep();
      }, this.#config.options.sleepTimeout);
    }
  }
  #canSleep() {
    if (!this.#ready) return 1 /* NotReady */;
    if (!this.#started) return 1 /* NotReady */;
    if (this.#activeHonoHttpRequests > 0)
      return 4 /* ActiveHonoHttpRequests */;
    if (this.#activeKeepAwakeCount > 0) return 5 /* ActiveKeepAwake */;
    for (const _conn of this.connectionManager.connections.values()) {
      return 3 /* ActiveConns */;
    }
    return 0 /* Yes */;
  }
  get #sleepingSupported() {
    return this.driver.startSleep !== void 0;
  }
  get #varsEnabled() {
    return "createVars" in this.#config || "vars" in this.#config;
  }
  #validateVarsEnabled() {
    if (!this.#varsEnabled) {
      throw new VarsNotEnabled();
    }
  }
};

// src/actor/definition.ts
var ActorDefinition = class {
  #config;
  constructor(config) {
    this.#config = config;
  }
  get config() {
    return this.#config;
  }
  instantiate() {
    return new ActorInstance(this.#config);
  }
};
function lookupInRegistry(config, name) {
  const definition = config.use[name];
  if (!definition) throw new Error(`no actor in registry for name ${name}`);
  return definition;
}

// src/client/mod.ts
function createClient(endpointOrConfig) {
  const configInput = endpointOrConfig === void 0 ? {} : typeof endpointOrConfig === "string" ? { endpoint: endpointOrConfig } : endpointOrConfig;
  const config = ClientConfigSchema.parse(configInput);
  const driver = new RemoteManagerDriver(config);
  if (config.devtools) {
    injectDevtools(config);
  }
  return createClientWithDriver(driver, config);
}

export {
  ActorDefinition,
  lookupInRegistry,
  ActorClientError,
  InternalError2 as InternalError,
  ManagerError,
  MalformedResponseMessage,
  ActorError,
  ActorConnDisposed,
  ActorConnRaw,
  ActorHandleRaw,
  createClientWithDriver,
  ClientConfigSchemaBase,
  ClientConfigSchema,
  transformClientConfig,
  convertRegistryConfigToClientConfig,
  getEndpoint,
  getDatacenters,
  updateRunnerConfig,
  RemoteManagerDriver,
  createClient
};
//# sourceMappingURL=chunk-OKJPXPJO.js.map